import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { getAuthToken, saveApplicationFormDraft, clearApplicationFormDraft } from 'utils/storage';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Paper,
  Divider,
  Box,
  Avatar,
  IconButton,
  Alert,
  CircularProgress,
  ListSubheader,
  Collapse,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO, isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';
import CasteOptions from '../enum/CasteOptions';
import { useSubjects, useSubjectsByClassId, useVocationalSubjects } from '../../hooks/useSubjects';
import { useClasses } from '../../hooks/useClasses';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { useApplicationForm, useApplicationPhotoInfo } from '../../hooks/useApplicationForm';
import { useStudentPhotoInfo } from '../../hooks/useStudents';
import { useError } from '../../contexts/ErrorContext';
import { useAuth } from '../../contexts/AuthContext';
import { useMarkSheets } from '../../hooks/useMarkSheets';
import { useHouse } from '../../hooks/useHouse';
import { ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED } from '../enum/SubjectOptions';

const steps = [
  'application_enrollment_details',
  'personal_information',
  'contact_address',
  'bank_financial',
  'admission_details',
  'subjects_academic'
];

// Subject options are now dynamically generated based on class data

export interface ApplicationFormData {
  id?: number;
  // Step 1: Application & Enrollment Details
  applicationNo: string;
  rollNo: string;
  enrolmentNo: string;
  scholarNo: string;
  sssmid: string;
  penNo: string;
  apparId: string;
  studentCode?: string; // Student code field

  // Step 2: Student Personal Information
  studentName: string;
  fatherName: string;
  motherName: string;
  dateOfBirth: string;
  gender: string;
  religion: string;
  caste: string;
  subCaste: string;
  casteCertificate: string;
  aadharNo: string;
  studentPhoto?: File | null; // For new uploads
  studentPhotoUrl?: string; // For existing photos (from backend)
  studentSignature?: File | null; // For new signature uploads
  studentSignatureUrl?: string; // For existing signatures (from backend)

  // Step 3: Contact & Address Information
  houseNo: string;
  street: string;
  landmark: string;
  area: string;
  city: string;
  postOffice: string;
  district: string;
  state: string;
  pinCode: string;
  mobileNo: string;
  gmail: string;

  // Step 4: Bank & Financial Information
  bankName: string;
  ifcCode: string;
  accountNo: string;
  annualIncome: string;

  // Step 5: Admission Details
  admissionDate: string;
  admissionClass: string;
  section: string;
  medium: string;
  admissionType: string; // Admission Type field
  stream: string; // Stream field
  cwsnCode: string; // CWSN Code field
  sambalKramank: string; // Sambal Kramank field
  house: string; // House code (school house, not address houseNo)
  houseId?: number | string | null;
  rte?: string; // RTE field

  // Step 6: Subjects - Backend expects List<String> (array of subject codes)
  subjects: string[];

  // System fields
  schoolId: number;
  isRegistered: string; // "pending" | "approved" | "rejected"
  sessionId: number;
  sessionName?: string;
  isSibling?: boolean;
}

export const ApplicationFormModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: ApplicationFormData) => void;
  initialData?: ApplicationFormData;
  isEdit?: boolean;
  isStudentEdit?: boolean; // New prop to identify when editing students
  isStudentProfileUpdate?: boolean; // New prop to identify when student is updating their own profile
  isReadOnly?: boolean; // New prop to make form read-only (for school admin/teacher viewing)
  hideAdmissionDetails?: boolean; // New prop to hide admission details section
  schoolData?: {
    nameofSchool?: string;
    blockName?: string;
    district?: string;
    diseCode?: string;
    state?: string;
    pinNo?: string;
    schoolCode?: string;
    schoolLogo?: string;
    medium?: string | string[];
  };

  selectedClass?: {
    id?: number;
    name: string;
    section: string;
    schoolId: number;
    sessionId?: number;
  } | null;
}> = ({ open, onClose, onSubmit, initialData, isEdit, isStudentEdit, isStudentProfileUpdate = false, isReadOnly = false, hideAdmissionDetails = false, schoolData, selectedClass }) => {
  const { t } = useTranslation();
  const { showError } = useError();
  const { isSchoolAdmin, isTeacher } = useAuth();

  // Get all subjects and filter by class - this ensures we get the latest data when subjects are added/deleted
  const schoolId =
    (selectedClass as any)?.schoolId ||
    selectedClass?.schoolId ||
    0;
  const { classesQuery } = useClasses(schoolId ? schoolId : undefined);
  const { getAllHouseQuery } = useHouse();
  const { subjectsQuery } = useSubjects(
    false, // tenantLoading is not needed for this hook
    schoolId ? schoolId : undefined
  );

  const allSubjects = subjectsQuery.data;
  // Prefer classId-based endpoint for reliability (avoids className string mismatches)
  // Guard against UI-only synthetic class IDs (e.g. "*-ALL" uses negative ids).
  const safeSelectedClassId =
    typeof selectedClass?.id === "number" && selectedClass.id > 0 ? selectedClass.id : 0;
  const subjectsByClassIdQuery = useSubjectsByClassId(safeSelectedClassId, false);
  const subjectsLoading = subjectsQuery.isLoading || subjectsByClassIdQuery.isLoading;

  // Fetch vocational subjects for the selected class
  const { data: vocationalSubjects } = useVocationalSubjects(
    safeSelectedClassId || null,
    false
  );

  // Filter subjects by selected class
  const classSubjects = React.useMemo(() => {
    if (!selectedClass) return [];
    const byId = subjectsByClassIdQuery.data;
    if (Array.isArray(byId) && byId.length > 0) {
      // Some class objects don't include schoolId; only filter when we have it.
      return schoolId ? byId.filter((s: any) => s?.schoolId === schoolId) : byId;
    }
    // Fallback: old className-based filtering when classId API has no data
    if (!allSubjects) return [];
    return allSubjects.filter((subject: any) => {
      const matchesClass =
        subject.className === selectedClass.name ||
        subject.className === `${selectedClass.name} - ${selectedClass.section}` ||
        subject.className === selectedClass.name?.replace(/\d+/, (match: string) => match + 'th');
      const matchesSchool = schoolId ? subject.schoolId === schoolId : true;
      return matchesClass && matchesSchool;
    });
  }, [allSubjects, selectedClass, schoolId, subjectsByClassIdQuery.data]);

  // Get photo info when editing (only if we have an ID)
  // Call both hooks unconditionally to follow Rules of Hooks
  const { data: studentPhotoInfo, isLoading: studentPhotoLoading } = useStudentPhotoInfo(
    isStudentEdit && isEdit && initialData?.id ? initialData.id : undefined
  );
  const { data: applicationPhotoInfo, isLoading: applicationPhotoLoading } = useApplicationPhotoInfo(
    !isStudentEdit && isEdit && initialData?.id ? initialData.id : undefined
  );

  // Check if student has marksheets (to disable subject editing)
  const { useMarksheetByStudentId } = useMarkSheets();
  const { data: existingMarksheets } = useMarksheetByStudentId(
    isStudentEdit && isEdit && initialData?.id ? initialData.id : null,
    isStudentEdit && isEdit && !!initialData?.id
  );

  const hasMarksheets = existingMarksheets && existingMarksheets.length > 0;
  // Only lock subjects based on marksheets when editing a STUDENT.
  // For application forms (add/edit), subjects should remain editable.
  const shouldLockSubjectsForMarksheets = isStudentEdit && isEdit && hasMarksheets;

  // Use the appropriate photo info based on context
  const photoInfo = isStudentEdit ? studentPhotoInfo : applicationPhotoInfo;
  const photoLoading = isStudentEdit ? studentPhotoLoading : applicationPhotoLoading;

  // Extract signature info: 
  // - First check if initialData has studentSignature as a filename (string)
  // - For students: get from photoInfo.signature field
  // - For applications: get from photoInfo.studentSignature field
  const signatureFileName = (typeof initialData?.studentSignature === 'string' && initialData.studentSignature)
    ? initialData.studentSignature
    : (isStudentEdit
      ? (studentPhotoInfo?.signature || null)
      : (applicationPhotoInfo?.studentSignature || null));

  // State for authenticated image URL
  const [authenticatedImageUrl, setAuthenticatedImageUrl] = useState<string | null>(null);
  const [imagePreviewModal, setImagePreviewModal] = useState<boolean>(false);

  // State for authenticated signature URL
  const [authenticatedSignatureUrl, setAuthenticatedSignatureUrl] = useState<string | null>(null);
  const [signaturePreviewModal, setSignaturePreviewModal] = useState<boolean>(false);

  // Debug logging for photo info and create authenticated image URL
  useEffect(() => {
    // First check if initialData has studentPhoto as a filename (string)
    // If not, fall back to photoInfo from API
    const photoFileName = (typeof initialData?.studentPhoto === 'string' && initialData.studentPhoto)
      ? initialData.studentPhoto
      : (photoInfo?.fileName || null);

    // Only proceed if we have a photo fileName
    if (photoFileName && isEdit && open) {
      // Use appropriate API endpoint based on whether we're editing a student or application form
      const constructedUrl = isStudentEdit
        ? `${process.env.REACT_APP_API_URL}/students/photo/${photoFileName}`
        : `${process.env.REACT_APP_API_URL}/application-forms/photo/${photoFileName}`;

      // Create authenticated image URL with token
      const token = getAuthToken();
      if (token) {
        // Create a blob URL with authenticated request
        // Use native fetch with error handling to prevent logout on 404/401/403
        fetch(constructedUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Tenant-ID': String(axios.defaults.headers.common['X-Tenant-ID'] || '')
          }
        })
          .then(response => {

            // Handle 404 gracefully - student/application might not have a photo
            if (response.status === 404) {
              setAuthenticatedImageUrl(null);
              return null;
            }

            // Handle 401/403 gracefully - don't trigger logout, just show no photo
            if (response.status === 401 || response.status === 403) {
              setAuthenticatedImageUrl(null);
              return null;
            }

            if (response.ok) {
              return response.blob();
            } else {
              setAuthenticatedImageUrl(null);
              return null;
            }
          })
          .then(blob => {
            // Only create blob URL if we got a valid blob
            if (blob) {
              const imageUrl = URL.createObjectURL(blob);
              setAuthenticatedImageUrl(imageUrl);
            }
          })
          .catch(error => {
            // Silently handle errors - don't trigger logout
            setAuthenticatedImageUrl(null);
          });
      } else {
        setAuthenticatedImageUrl(null);
      }
    } else if (isEdit && open && !photoFileName) {
      // If no photoFileName (null), clear the image URL
      setAuthenticatedImageUrl(null);
    } else if (!isEdit || !open) {
      // Clear image URL when not editing or modal is closed
      setAuthenticatedImageUrl(null);
    }
  }, [initialData?.studentPhoto, photoInfo, isEdit, open, isStudentEdit]);

  // Debug logging for signature info and create authenticated signature URL
  useEffect(() => {
    // Only proceed if we have a signature fileName (can be null if no signature exists)
    if (signatureFileName && isEdit && open) {
      // Use appropriate API endpoint based on whether we're editing a student or application form
      // Both use /photo/{fileName} endpoint
      const constructedUrl = isStudentEdit
        ? `${process.env.REACT_APP_API_URL}/students/photo/${signatureFileName}`
        : `${process.env.REACT_APP_API_URL}/application-forms/photo/${signatureFileName}`;

      // Create authenticated signature URL with token
      const token = getAuthToken();
      if (token) {
        // Create a blob URL with authenticated request
        // Use native fetch with error handling to prevent logout on 404/401/403
        fetch(constructedUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Tenant-ID': String(axios.defaults.headers.common['X-Tenant-ID'] || '')
          }
        })
          .then(response => {

            // Handle 404 gracefully - student/application might not have a signature
            if (response.status === 404) {
              setAuthenticatedSignatureUrl(null);
              return null;
            }

            // Handle 401/403 gracefully - don't trigger logout, just show no signature
            if (response.status === 401 || response.status === 403) {
              setAuthenticatedSignatureUrl(null);
              return null;
            }

            if (response.ok) {
              return response.blob();
            } else {
              setAuthenticatedSignatureUrl(null);
              return null;
            }
          })
          .then(blob => {
            // Only create blob URL if we got a valid blob
            if (blob) {
              const signatureUrl = URL.createObjectURL(blob);
              setAuthenticatedSignatureUrl(signatureUrl);
            }
          })
          .catch(error => {
            // Silently handle errors - don't trigger logout
            setAuthenticatedSignatureUrl(null);
          });
      } else {
        setAuthenticatedSignatureUrl(null);
      }
    } else if (isEdit && open && !signatureFileName) {
      // If no signatureFileName (null), clear the signature URL
      setAuthenticatedSignatureUrl(null);
    } else if (!isEdit || !open) {
      // Clear signature URL when not editing or modal is closed
      setAuthenticatedSignatureUrl(null);
    }
  }, [signatureFileName, isEdit, open, isStudentEdit]);

  // Cleanup blob URL on unmount or when switching between different applications
  useEffect(() => {
    return () => {
      if (authenticatedImageUrl) {
        URL.revokeObjectURL(authenticatedImageUrl);
      }
      if (authenticatedSignatureUrl) {
        URL.revokeObjectURL(authenticatedSignatureUrl);
      }
    };
  }, [initialData?.id]); // Clean up when switching between different applications

  // Helper function to normalize subjects to always be an array
  const normalizeSubjects = useCallback((subjects: string[] | string | undefined): string[] => {
    if (!subjects) return [];
    if (Array.isArray(subjects)) return subjects;
    if (typeof subjects === 'string') return subjects.split(',').map(s => s.trim()).filter(Boolean);
    return [];
  }, []);

  // Helper function to safely parse date strings and arrays (including dd/MM/yyyy format and array format)
  const safeParseDateString = useCallback((dateInput: string | number[] | null | undefined): Date | null => {
    if (!dateInput) return null;

    try {
      // Handle array format dates [year, month, day] - common from APIs
      if (Array.isArray(dateInput) && dateInput.length >= 3) {
        const [year, month, day] = dateInput;
        if (typeof year === 'number' && typeof month === 'number' && typeof day === 'number') {
          // Note: Backend sends month as 1-indexed (1=Jan, 12=Dec), so we use it directly
          const date = new Date(year, month - 1, day);
          if (isValid(date)) {
            return date;
          }
        }
      }

      // Handle string format dates
      if (typeof dateInput === 'string') {
        // First try to parse as dd/MM/yyyy format (as expected by backend)
        if (dateInput.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
          const [day, month, year] = dateInput.split('/');
          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          if (isValid(date)) {
            return date;
          }
        }

        // Try to parse as ISO date (YYYY-MM-DD)
        const isoDate = parseISO(dateInput);
        if (isValid(isoDate)) {
          return isoDate;
        }

        // If ISO parsing fails, try to create a date directly
        const directDate = new Date(dateInput);
        if (isValid(directDate)) {
          return directDate;
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  }, []);

  const [siblingDialogOpen, setSiblingDialogOpen] = useState(false);
  const [siblingStudentName, setSiblingStudentName] = useState('');

  const [formData, setFormData] = useState<ApplicationFormData>(initialData ? {
    ...initialData,
    studentCode: initialData.studentCode || '',
    subjects: normalizeSubjects(initialData.subjects),
    isRegistered: typeof initialData.isRegistered === 'boolean'
      ? (initialData.isRegistered ? 'Approved' : 'Pending')
      : initialData.isRegistered,
    houseId: initialData.houseId || ''
  } : {
    applicationNo: '',
    rollNo: '',
    enrolmentNo: '',
    scholarNo: '',
    sssmid: '',
    penNo: '',
    apparId: '',
    studentCode: '',
    studentName: '',
    fatherName: '',
    motherName: '',
    dateOfBirth: '',
    gender: '',
    religion: '',
    caste: '',
    subCaste: '',
    casteCertificate: '',
    aadharNo: '',
    studentPhoto: null, // Initialize photo as null
    studentSignature: null, // Initialize signature as null
    houseNo: '',
    street: '',
    landmark: '',
    area: '',
    city: '',
    postOffice: '',
    district: '',
    state: '',
    pinCode: '',
    mobileNo: '',
    gmail: '',
    bankName: '',
    ifcCode: '',
    accountNo: '',
    annualIncome: '',
    admissionDate: '',
    admissionClass: selectedClass?.name || '', // Auto-populate from selected class
    section: selectedClass?.section || '', // Auto-populate from selected section
    medium: '',
    admissionType: '',
    stream: '', // Initialize stream as empty string
    cwsnCode: '',
    sambalKramank: '',
    house: '',
    houseId: '',
    rte: '',
    subjects: [], // Initialize as empty array
    schoolId: 0,
    isRegistered: "Pending",
    sessionId: 0,
  });

  // State for image preview
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string>('');

  // State for signature preview
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const [signatureError, setSignatureError] = useState<string>('');

  // State for subject select dropdown value
  const [subjectSelectValue, setSubjectSelectValue] = useState<string>('');

  // State for controlling dropdown open/close
  const [subjectDropdownOpen, setSubjectDropdownOpen] = useState<boolean>(false);

  // State for managing expanded groups and subgroups (for Class 11th/12th accordion UI)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedSubgroups, setExpandedSubgroups] = useState<Set<string>>(new Set());

  // New state for Class 11th/12th subject selection rules
  const [selectedMainGroup, setSelectedMainGroup] = useState<string | null>(null); // Selected main group (only one)
  const [languageGroupExpanded, setLanguageGroupExpanded] = useState<boolean>(true); // Language group expanded by default
  const [selectedStream, setSelectedStream] = useState<string>(''); // Selected stream (maps to group code 1-8)

  // Stream options mapping (Group Code -> Group Name)
  const streamOptions = [
    { value: '1', label: 'HUMANITIES GROUP', groupName: 'HUMANITIES GROUP' },
    { value: '2', label: 'SCIENCE MATHS GROUP', groupName: 'SCIENCE MATHS GROUP' },
    { value: '3', label: 'COMMERCE GROUP', groupName: 'COMMERCE GROUP' },
    { value: '4', label: 'AGRICULTURE GROUP', groupName: 'AGRICULTURE GROUP' },
    { value: '5', label: 'FINE ART GROUP', groupName: 'FINE ART GROUP' },
    { value: '6', label: 'HOME SCIENCE GROUP', groupName: 'HOME SCIENCE GROUP' },
    { value: '7', label: 'SCIENCE BIO. GROUP', groupName: 'SCIENCE BIO. GROUP' },
    { value: '8', label: 'LANGUAGE GROUP', groupName: 'LANGUAGE GROUP' },
  ];

  // Ref to prevent double selection when handling manually (using ref to avoid callback dependency issues)
  const isManualSelectionRef = React.useRef<boolean>(false);

  // State for field validation errors
  const [fieldErrors, setFieldErrors] = useState<{
    studentName?: boolean;
    fatherName?: boolean;
    motherName?: boolean;
    gender?: boolean;
    dateOfBirth?: boolean;
    mobileNo?: boolean | string;
    medium?: boolean;
    gmail?: boolean | string;
    ifcCode?: boolean | string;
    accountNo?: boolean | string;
  }>({});

  // Image handling functions
  const handleImageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setImageError(t("please_select_valid_image_file"));
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setImageError(t("image_size_should_be_less_than_5mb"));
        return;
      }

      setImageError('');
      setFormData(prev => ({ ...prev, studentPhoto: file }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleRemoveImage = useCallback(() => {
    setFormData(prev => ({ ...prev, studentPhoto: null, studentPhotoUrl: undefined }));
    setImagePreview(null);
    setImageError('');
    setAuthenticatedImageUrl(null);
  }, []);

  // Signature handling functions
  const handleSignatureChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setSignatureError(t("please_select_valid_image_file"));
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setSignatureError(t("image_size_should_be_less_than_5mb"));
        return;
      }

      setSignatureError('');
      setFormData(prev => ({ ...prev, studentSignature: file }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setSignaturePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleRemoveSignature = useCallback(() => {
    setFormData(prev => ({ ...prev, studentSignature: null, studentSignatureUrl: undefined }));
    setSignaturePreview(null);
    setSignatureError('');
    setAuthenticatedSignatureUrl(null);
  }, []);

  // Function to get available medium options based on school's medium
  const getAvailableMediumOptions = useCallback(() => {
    const options: { value: string; label: string }[] = [];

    // Always include the current form data's medium if it exists (for editing)
    if (formData.medium && !options.find(opt => opt.value === formData.medium)) {
      options.push({
        value: formData.medium,
        label: formData.medium
      });
    }

    if (!schoolData?.medium) {
      // If no school data, return current medium or default options
      return options.length > 0 ? options : [
        { value: "English", label: "English" },
        { value: "Hindi", label: "Hindi" }
      ];
    }

    const schoolMedium = schoolData.medium;

    // Handle array of mediums (new backend format)
    if (Array.isArray(schoolMedium)) {
      schoolMedium.forEach(medium => {
        if (!options.find(opt => opt.value === medium)) {
          options.push({
            value: medium,
            label: medium
          });
        }
      });
    }

    // Handle string format (legacy support)
    if (typeof schoolMedium === 'string') {
      // If school medium is "ENGLISH & HINDI", show both options
      if (schoolMedium === "ENGLISH & HINDI") {
        if (!options.find(opt => opt.value === "English")) {
          options.push({ value: "English", label: "English" });
        }
        if (!options.find(opt => opt.value === "Hindi")) {
          options.push({ value: "Hindi", label: "Hindi" });
        }
      }
      // If school medium is "ENGLISH", show only English
      else if (schoolMedium === "ENGLISH") {
        if (!options.find(opt => opt.value === "English")) {
          options.push({ value: "English", label: "English" });
        }
      }
      // If school medium is "HINDI", show only Hindi
      else if (schoolMedium === "HINDI") {
        if (!options.find(opt => opt.value === "Hindi")) {
          options.push({ value: "Hindi", label: "Hindi" });
        }
      }
      // Handle comma-separated mediums like "Hindi, English"
      else if (schoolMedium.includes(',')) {
        const mediums = schoolMedium.split(',').map(m => m.trim());
        mediums.forEach(medium => {
          if (medium && !options.find(opt => opt.value === medium)) {
            options.push({ value: medium, label: medium });
          }
        });
      }
      // Single medium string
      else {
        if (!options.find(opt => opt.value === schoolMedium)) {
          options.push({ value: schoolMedium, label: schoolMedium });
        }
      }
    }

    // If no options found, add default options
    if (options.length === 0) {
      options.push(
        { value: "English", label: "English" },
        { value: "Hindi", label: "Hindi" }
      );
    }

    return options;
  }, [schoolData?.medium, formData.medium]);

  // Function to get default medium based on school's medium
  const getDefaultMedium = useCallback(() => {
    // Always return empty string to let user choose from dropdown
    // This ensures the dropdown is always shown with all available options
    return "";
  }, []);

  // Update form data when initialData changes or modal opens
  useEffect(() => {

    if (open) {
      if (initialData) {

        // Transform gender data to ensure proper mapping
        const transformGender = (gender: string | null | undefined): string => {
          if (!gender) return '';
          const genderLower = gender.toLowerCase().trim();
          if (genderLower === 'm' || genderLower === 'male') return 'M';
          if (genderLower === 'f' || genderLower === 'female') return 'F';
          if (genderLower === 'o' || genderLower === 'other') return 'O';
          return gender; // Return original value if not recognized
        };

        // Transform admission type data to ensure proper mapping
        const transformAdmissionType = (admissionType: string | null | undefined): string => {
          if (!admissionType) return '';
          const typeLower = admissionType.toLowerCase().trim();
          if (typeLower === 'regular') return 'REGULAR';
          if (typeLower === 'private') return 'PRIVATE';
          return admissionType; // Return original value if not recognized
        };

        // Convert any existing dates to proper format (no defaults)
        let dateOfBirth = '';
        let admissionDate = '';

        if (initialData.dateOfBirth) {
          const parsedDate = safeParseDateString(initialData.dateOfBirth);
          if (parsedDate && isValid(parsedDate)) {
            dateOfBirth = format(parsedDate, 'dd/MM/yyyy');
          } else {
          }
        }

        if (initialData.admissionDate) {
          const parsedDate = safeParseDateString(initialData.admissionDate);
          if (parsedDate && isValid(parsedDate)) {
            admissionDate = format(parsedDate, 'dd/MM/yyyy');
          } else {
          }
        }

        const updatedFormData = {
          ...initialData,
          gender: transformGender(initialData.gender), // Transform gender for proper mapping
          admissionType: transformAdmissionType(initialData.admissionType), // Transform admission type for proper mapping
          subjects: normalizeSubjects(initialData.subjects), // Normalize subjects to array
          dateOfBirth: dateOfBirth, // Use parsed date or empty string
          admissionDate: admissionDate, // Use parsed date or empty string
          studentPhoto: null, // Reset photo for editing (will need to be re-uploaded)
          studentSignature: null, // Reset signature for editing (will need to be re-uploaded)
          isRegistered: typeof initialData.isRegistered === 'boolean'
            ? (initialData.isRegistered ? 'Approved' : 'Pending')
            : initialData.isRegistered,
          houseId: initialData.houseId || ''
        };

        setFormData(updatedFormData as ApplicationFormData);
      } else {
        // Get default medium based on school's medium
        const defaultMedium = getDefaultMedium();

        // Reset to default values when adding new form (no default dates)
        setFormData({
          applicationNo: '',
          rollNo: '',
          enrolmentNo: '',
          scholarNo: '',
          sssmid: '',
          penNo: '',
          apparId: '',
          studentCode: '',
          studentName: '',
          fatherName: '',
          motherName: '',
          dateOfBirth: '',
          gender: '',
          religion: '',
          caste: '',
          subCaste: '',
          casteCertificate: '',
          aadharNo: '',
          studentPhoto: null, // Reset photo
          studentPhotoUrl: undefined, // Reset photo URL
          studentSignature: null, // Reset signature
          studentSignatureUrl: undefined, // Reset signature URL
          houseNo: '',
          street: '',
          landmark: '',
          area: '',
          city: '',
          postOffice: '',
          district: '',
          state: '',
          pinCode: '',
          mobileNo: '',
          gmail: '',
          bankName: '',
          ifcCode: '',
          accountNo: '',
          annualIncome: '',
          admissionDate: '',
          admissionClass: selectedClass?.name || '', // Auto-populate from selected class
          section: selectedClass?.section || '', // Auto-populate from selected section
          medium: defaultMedium, // Set default medium based on school's medium
          admissionType: '',
          stream: '', // Initialize stream as empty string
          cwsnCode: '',
          sambalKramank: '',
          house: '',
          houseId: '',
          rte: '',
          subjects: [], // Initialize as empty array
          schoolId: 0,
          isRegistered: "Pending",
          sessionId: 0,
        } as ApplicationFormData);
      }
    }
  }, [open, initialData, selectedClass, normalizeSubjects, safeParseDateString, getDefaultMedium]);


  // Reset image preview when modal opens for new forms or different applications
  useEffect(() => {
    if (open) {
      // Always reset photo state when modal opens
      setImagePreview(null);
      setImageError('');
      // Reset signature state when modal opens
      setSignaturePreview(null);
      setSignatureError('');
      // Reset field errors when modal opens
      setFieldErrors({});
      // Reset subject select value
      setSubjectSelectValue('');
      // Close dropdown when modal opens
      setSubjectDropdownOpen(false);
      // Note: authenticatedImageUrl and authenticatedSignatureUrl are managed by their respective fetch effects
      // They will be set when signature/photo info is available, or cleared if not available
    }
  }, [open, initialData?.id]); // Reset when modal opens or when editing different application

  // Function to get available subjects for the selected class
  const getAvailableSubjects = useCallback(() => {

    if (!selectedClass || !classSubjects) {
      return [];
    }

    // Convert API subjects to the format expected by the form
    const availableSubjects = classSubjects.map((subject: any) => ({
      value: subject.subjectCode, // e.g., "401", "100", "200"
      label: `(${subject.subjectCode}) ${subject.name}` // e.g., "(401) Hindi", "(100) Maths (Standard)"
    }));

    return availableSubjects;
  }, [selectedClass, classSubjects]);

  // Helper function to identify languages (by subject code or name)
  const isFirstLanguage = useCallback((subjectCode: string, subjectName: string): boolean => {
    // Common language subject codes (typically 400-500 range)
    const firstLanguageCodes = ['401', '411', '502', '504', '503', '505', '506', '507', '508', '509'];

    // Check by code
    if (firstLanguageCodes.includes(subjectCode)) {
      return true;
    }

    // Check by name (case-insensitive)
    const normalizedName = subjectName.toLowerCase();
    const languageKeywords = ['hindi', 'english', 'marathi', 'gujarati', 'sanskrit', 'urdu', 'bengali', 'telugu', 'tamil', 'kannada', 'malayalam', 'punjabi', 'odia'];

    return languageKeywords.some(keyword => normalizedName.includes(keyword));
  }, []);

  // Check if class is 11th or 12th
  const isEleventhTwelfthClass = React.useMemo(() => {
    if (!selectedClass?.name) return false;
    const classNumber = selectedClass.name.match(/\d+/)?.[0];
    return classNumber === '11' || classNumber === '12';
  }, [selectedClass?.name]);

  // Function to find which group a subject belongs to (for Class 11th/12th)
  const findSubjectGroup = useCallback((subjectCode: string, subjectName: string) => {
    if (!isEleventhTwelfthClass) return null;

    for (const group of ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED) {
      const found = group.subjects.find(s =>
        s.code === subjectCode || s.name === subjectName
      );
      if (found) {
        return group.groupName;
      }
    }
    return null;
  }, [isEleventhTwelfthClass]);

  // Helper function to process a group and return its structure
  const processGroup = useCallback((group: any, classSubjects: any[]): any | null => {
    if (group.hasSubgroups && group.subgroups) {
      // Check if ALL subjects in ALL subgroups exist in classSubjects
      let allSubgroupsComplete = true;
      const subgroupsData: any[] = [];

      group.subgroups.forEach((subgroup: any) => {
        const allSubjectsInSubgroupExist = subgroup.subjects.every((subgroupSubject: any) =>
          classSubjects.some((cs: any) =>
            cs.subjectCode === subgroupSubject.code || cs.name === subgroupSubject.name
          )
        );

        if (allSubjectsInSubgroupExist) {
          // Map all subjects from this subgroup to available classSubjects
          const subgroupSubjects: any[] = [];
          subgroup.subjects.forEach((subgroupSubject: any) => {
            const availableSubject = classSubjects.find((cs: any) =>
              cs.subjectCode === subgroupSubject.code || cs.name === subgroupSubject.name
            );
            if (availableSubject) {
              // Create unique value with group name prefix to treat same subject in different groups as different
              // Use the definition code for consistency with filtering logic
              const codeToUse = subgroupSubject.code || availableSubject.subjectCode;
              const uniqueValue = `${group.groupName}|${codeToUse}`;
              subgroupSubjects.push({
                value: uniqueValue,
                label: `(${subgroupSubject.code}) ${subgroupSubject.name}`,
                subjectName: availableSubject.name,
                subjectCode: codeToUse, // Use definition code for consistency
                originalCode: subgroupSubject.code, // Store original code from definition
                databaseCode: availableSubject.subjectCode, // Store database code for reference
                groupName: group.groupName // Store group name for reference
              });
            }
          });

          subgroupsData.push({
            subgroupName: subgroup.subgroupName,
            description: subgroup.description,
            subjects: subgroupSubjects
          });
        } else {
          allSubgroupsComplete = false;
        }
      });

      // Only return group if ALL subgroups are complete
      if (allSubgroupsComplete && subgroupsData.length > 0) {
        return {
          hasSubgroups: true,
          groupCode: group.groupCode,
          description: group.description,
          subgroups: subgroupsData,
          subjects: subgroupsData.flatMap((sg: any) => sg.subjects)
        };
      }
      return null;
    } else {
      // Handle groups without subgroups
      const allSubjectsExist = group.subjects.every((groupSubject: any) =>
        classSubjects.some((cs: any) =>
          cs.subjectCode === groupSubject.code || cs.name === groupSubject.name
        )
      );

      // Only return group if ALL subjects are created
      if (allSubjectsExist) {
        const groupSubjects: any[] = [];
        group.subjects.forEach((groupSubject: any) => {
          const availableSubject = classSubjects.find((cs: any) =>
            cs.subjectCode === groupSubject.code || cs.name === groupSubject.name
          );
          if (availableSubject) {
            // Create unique value with group name prefix to treat same subject in different groups as different
            // Use the definition code for consistency with filtering logic
            const codeToUse = groupSubject.code || availableSubject.subjectCode;
            const uniqueValue = `${group.groupName}|${codeToUse}`;
            groupSubjects.push({
              value: uniqueValue,
              label: `(${groupSubject.code}) ${groupSubject.name}`,
              subjectName: availableSubject.name,
              subjectCode: codeToUse, // Use definition code for consistency
              originalCode: groupSubject.code, // Store original code from definition
              databaseCode: availableSubject.subjectCode, // Store database code for reference
              groupName: group.groupName // Store group name for reference
            });
          }
        });

        return {
          hasSubgroups: false,
          groupCode: group.groupCode,
          description: group.description,
          subjects: groupSubjects
        };
      }
      return null;
    }
  }, []);

  // Function to categorize and filter subject options
  const getGroupedSubjectOptions = useCallback(() => {
    if (!selectedClass || !classSubjects) {
      return {
        firstLanguages: [],
        vocational: [],
        other: [],
        groups: {}, // For Class 11th/12th: groupName -> subjects[]
        languageGroup: null,
        mainGroups: {},
        additionalGroup: null,
        nsqfGroup: null
      };
    }

    // Convert API subjects to the format expected by the form
    const availableSubjects = classSubjects.map((subject: any) => ({
      value: subject.subjectCode,
      label: `(${subject.subjectCode}) ${subject.name}`,
      subjectName: subject.name,
      subjectCode: subject.subjectCode
    }));

    // For Class 11th/12th, separate into Language, Main Groups, Additional, and NSQF
    if (isEleventhTwelfthClass) {
      const mainGroupsMap: { [key: string]: any } = {};
      let additionalGroup: any = null;
      let nsqfGroup: any = null;

      // Get LANGUAGE group subjects first to filter them out from other groups
      const languageGroupDef = ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED.find(g => g.groupName === "LANGUAGE");
      const languageSubjectCodes = languageGroupDef?.subjects.map(s => s.code) || [];

      // Process each group from ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED
      ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED.forEach((group) => {
        if (group.groupName === "LANGUAGE") {
          // Language group is handled separately
          return;
        }

        const processedGroup = processGroup(group, classSubjects);
        if (!processedGroup) return;

        // For LANGUAGE GROUP (main group), filter out subjects that are in LANGUAGE group (first step)
        if (group.groupName === "LANGUAGE GROUP") {
          // Filter out language subjects from the processed group's main subjects array
          const filteredGroup = {
            ...processedGroup,
            subjects: processedGroup.subjects?.filter((subject: any) =>
              !languageSubjectCodes.includes(subject.subjectCode)
            ) || []
          };

          // Also filter subgroups if they exist
          // IMPORTANT: Don't filter Subgroup A - let the rendering logic handle it based on selected languages
          // Only filter Subgroup B and other subgroups
          if (filteredGroup.hasSubgroups && filteredGroup.subgroups) {
            filteredGroup.subgroups = filteredGroup.subgroups.map((subgroup: any) => {
              // Keep all subjects in Subgroup A - filtering will happen at render time based on selected languages
              if (subgroup.subgroupName === "A") {
                return subgroup; // Don't filter Subgroup A
              }
              // Filter language subjects from other subgroups (like Subgroup B)
              return {
                ...subgroup,
                subjects: subgroup.subjects?.filter((subject: any) =>
                  !languageSubjectCodes.includes(subject.subjectCode)
                ) || []
              };
            });
          }

          // Only add if there are subjects left after filtering
          // For LANGUAGE GROUP, we always want to show it if Subgroup A has subjects (even if filtered later)
          if (filteredGroup.subjects.length > 0 ||
            (filteredGroup.subgroups && filteredGroup.subgroups.some((sg: any) => sg.subjects.length > 0))) {
            mainGroupsMap[group.groupName] = {
              groupName: group.groupName,
              ...filteredGroup
            };
          }
        } else if (group.groupName === "ADDITIONAL SUBJECT (OPTIONAL)") {
          // Add group prefix to Additional subjects
          const additionalSubjects = (processedGroup.subjects || []).map((subject: any) => ({
            ...subject,
            value: `ADDITIONAL SUBJECT (OPTIONAL)|${subject.subjectCode}`,
            groupName: group.groupName
          }));
          additionalGroup = {
            groupName: group.groupName,
            ...processedGroup,
            subjects: additionalSubjects
          };
        } else if (group.groupName === "NSQF SUBJECT (OPTIONAL)") {
          // Add group prefix to NSQF subjects
          const nsqfSubjects = (processedGroup.subjects || []).map((subject: any) => ({
            ...subject,
            value: `NSQF SUBJECT (OPTIONAL)|${subject.subjectCode}`,
            groupName: group.groupName
          }));
          nsqfGroup = {
            groupName: group.groupName,
            ...processedGroup,
            subjects: nsqfSubjects
          };
        } else {
          // Main groups (HUMANITIES, SCIENCE MATHS, COMMERCE, etc.)
          mainGroupsMap[group.groupName] = {
            groupName: group.groupName,
            ...processedGroup
          };
        }
      });

      // Process Language group separately
      if (languageGroupDef) {
        const processedLangGroup = processGroup(languageGroupDef, classSubjects);
        if (processedLangGroup) {
          // Filter to only show Subgroup A subjects: Hindi (051), English (052), Sanskrit (053), Marathi (054), Urdu (055)
          const subgroupASubjectCodes = ['051', '052', '053', '054', '055'];
          // Now subjectCode uses the definition code, so filtering should work correctly
          const langSubjects = (processedLangGroup.subjects || [])
            .filter((subject: any) => {
              // Use subjectCode which now contains the definition code
              return subgroupASubjectCodes.includes(subject.subjectCode);
            })
            .map((subject: any) => ({
              ...subject,
              value: `LANGUAGE|${subject.subjectCode}`,
              groupName: "LANGUAGE"
            }));
          const langGroupData = {
            groupName: "LANGUAGE",
            groupCode: null,
            description: languageGroupDef.description,
            subjects: langSubjects
          };
          return {
            firstLanguages: [],
            vocational: [],
            other: [],
            groups: mainGroupsMap, // Keep for backward compatibility
            languageGroup: langGroupData,
            mainGroups: mainGroupsMap,
            additionalGroup,
            nsqfGroup
          };
        }
      }

      return {
        firstLanguages: [],
        vocational: [],
        other: [],
        groups: mainGroupsMap, // Keep for backward compatibility
        languageGroup: null,
        mainGroups: mainGroupsMap,
        additionalGroup,
        nsqfGroup
      };
    }

    // For other classes, use the original categorization
    const firstLanguages: any[] = [];
    const vocational: any[] = [];
    const other: any[] = [];

    availableSubjects.forEach((subject: any) => {
      const normalizedSubjectName = subject.subjectName?.toLowerCase().trim() || '';
      const isVocational = vocationalSubjects?.has(normalizedSubjectName) ?? false;

      if (isVocational) {
        vocational.push(subject);
      } else if (isFirstLanguage(subject.subjectCode, subject.subjectName)) {
        firstLanguages.push(subject);
      } else {
        other.push(subject);
      }
    });

    return {
      firstLanguages,
      vocational,
      other,
      groups: {},
      languageGroup: null,
      mainGroups: {},
      additionalGroup: null,
      nsqfGroup: null
    };
  }, [selectedClass, classSubjects, vocationalSubjects, isFirstLanguage, isEleventhTwelfthClass, processGroup]);

  // Function to get filtered subject options (excluding already selected ones) - kept for backward compatibility
  const getFilteredSubjectOptions = useCallback(() => {
    const grouped = getGroupedSubjectOptions();
    // Return all subjects in a flat array for backward compatibility
    return [...grouped.firstLanguages, ...grouped.vocational, ...grouped.other];
  }, [getGroupedSubjectOptions]);

  const isThirdOrFourthClassByName = /^\s*[34]/.test(String(selectedClass?.name ?? ''));
  const maxSubjectsAllowed = isThirdOrFourthClassByName ? 4 : 6;

  // Function to add a new subject
  const handleAddSubject = useCallback((subjectValue: string) => {
    // Check if maximum limit is reached
    if (formData.subjects.length >= maxSubjectsAllowed) {
      return; // Don't add if limit is reached
    }
    const subject = getAvailableSubjects().find((s: any) => s.value === subjectValue);
    if (subject && !formData.subjects.includes(subject.value)) {
      setFormData(prev => ({
        ...prev,
        subjects: [...prev.subjects, subject.value] // Store only the code
      }));
    }
  }, [getAvailableSubjects, formData.subjects, maxSubjectsAllowed]);

  // Helper function to extract subject code from stored value (handles both "CODE" and "GROUP|CODE" formats)
  const extractSubjectCode = useCallback((storedValue: string): string => {
    const raw = (storedValue.includes('|') ? storedValue.split('|')[1] : storedValue) ?? '';
    const trimmed = String(raw).trim();
    // Normalize numeric codes like "51" -> "051" (common for subgroup language codes),
    // while keeping 3+ digit codes unchanged.
    const digitsOnly = trimmed.replace(/\D/g, '');
    if (digitsOnly.length === 2) return digitsOnly.padStart(3, '0');
    return digitsOnly || trimmed;
  }, []);

  // Backend should receive the full subject code (e.g. "HI101"), not only digits ("101").
  // Still normalize purely-numeric short codes like "51" -> "051".
  const getBackendSubjectCode = useCallback((storedValue: string): string => {
    const raw = (storedValue.includes('|') ? storedValue.split('|')[1] : storedValue) ?? '';
    const trimmed = String(raw).trim();
    if (/^\d+$/.test(trimmed)) {
      return trimmed.length === 2 ? trimmed.padStart(3, '0') : trimmed;
    }
    return trimmed;
  }, []);

  // Find a subject from `classSubjects` by code, always preferring an exact match (e.g. "TEST100")
  // and only falling back to numeric-only comparison if an exact match doesn't exist.
  const findSubjectByCode = useCallback((codeInput: string) => {
    if (!classSubjects) return undefined;
    const code = String(codeInput ?? '').trim();
    if (!code) return undefined;

    // 1) Exact match first (prevents "TEST100" being shadowed by "100")
    const exact = classSubjects.find((cs: any) => String(cs?.subjectCode ?? '').trim() === code);
    if (exact) return exact;

    // 2) Numeric fallback only if no exact match exists
    const codeDigits = extractSubjectCode(code);
    if (!codeDigits) return undefined;
    return classSubjects.find((cs: any) => extractSubjectCode(String(cs?.subjectCode ?? '')) === codeDigits);
  }, [classSubjects, extractSubjectCode]);

  // Function to remove a subject (by sorted index, finds actual subject in formData)
  const handleRemoveSubject = useCallback((sortedIndex: number) => {
    setFormData(prev => {
      // Get sorted subjects to find which subject to remove
      const sortedSubjects = (() => {
        if (!isEleventhTwelfthClass) {
          return prev.subjects;
        }
        // Use the same sorting logic as getSortedSubjects
        const sorted: string[] = [];
        const initialLanguages = prev.subjects.filter(storedValue =>
          storedValue.startsWith('LANGUAGE|') && !storedValue.startsWith('LANGUAGE GROUP|')
        );
        sorted.push(...initialLanguages);
        const mainGroupSubjects = prev.subjects.filter(storedValue => {
          if (storedValue.startsWith('LANGUAGE|') && !storedValue.startsWith('LANGUAGE GROUP|')) {
            return false;
          }
          if (storedValue.startsWith('ADDITIONAL SUBJECT (OPTIONAL)|') ||
            storedValue.startsWith('NSQF SUBJECT (OPTIONAL)|')) {
            return false;
          }
          return true;
        });
        const subjectsByGroup: { [key: string]: { subgroupA: string[], subgroupB: string[], other: string[] } } = {};
        mainGroupSubjects.forEach(storedValue => {
          const code = extractSubjectCode(storedValue);
          const groupName = storedValue.includes('|') ? storedValue.split('|')[0] : '';
          if (!subjectsByGroup[groupName]) {
            subjectsByGroup[groupName] = { subgroupA: [], subgroupB: [], other: [] };
          }
          if (groupName === 'LANGUAGE GROUP') {
            const subgroupACodes = ["051", "052", "053", "054", "055"];
            const subgroupBCodes = ["110", "120", "130", "140", "166"];
            if (subgroupACodes.includes(code)) {
              subjectsByGroup[groupName].subgroupA.push(storedValue);
            } else if (subgroupBCodes.includes(code)) {
              subjectsByGroup[groupName].subgroupB.push(storedValue);
            } else {
              subjectsByGroup[groupName].other.push(storedValue);
            }
          } else if (groupName === 'HUMANITIES GROUP') {
            const subgroupACodes = ["110", "120", "130", "140"];
            const subgroupBCodes = ["150", "151", "162", "163", "164", "165", "166", "167", "168"];
            if (subgroupACodes.includes(code)) {
              subjectsByGroup[groupName].subgroupA.push(storedValue);
            } else if (subgroupBCodes.includes(code)) {
              subjectsByGroup[groupName].subgroupB.push(storedValue);
            } else {
              subjectsByGroup[groupName].other.push(storedValue);
            }
          } else if (groupName === 'COMMERCE GROUP') {
            const subgroupACodes = ["150", "151"];
            const subgroupBCodes = ["140"];
            if (subgroupACodes.includes(code)) {
              subjectsByGroup[groupName].subgroupA.push(storedValue);
            } else if (subgroupBCodes.includes(code)) {
              subjectsByGroup[groupName].subgroupB.push(storedValue);
            } else {
              subjectsByGroup[groupName].other.push(storedValue);
            }
          } else {
            subjectsByGroup[groupName].other.push(storedValue);
          }
        });
        Object.keys(subjectsByGroup).sort().forEach(groupName => {
          const group = subjectsByGroup[groupName];
          sorted.push(...group.subgroupA);
          sorted.push(...group.subgroupB);
          sorted.push(...group.other);
        });
        const optionalSubjects = prev.subjects.filter(storedValue =>
          storedValue.startsWith('ADDITIONAL SUBJECT (OPTIONAL)|') ||
          storedValue.startsWith('NSQF SUBJECT (OPTIONAL)|')
        );
        sorted.push(...optionalSubjects);
        return sorted;
      })();

      // Get the subject to remove from sorted list
      const subjectToRemove = sortedSubjects[sortedIndex];

      // Remove it from the actual subjects array
      const newSubjects = prev.subjects.filter(subject => subject !== subjectToRemove);

      // If all subjects are removed, clear stream and selectedMainGroup
      if (newSubjects.length === 0) {
        setSelectedMainGroup(null);
        setSelectedStream('');
        return {
          ...prev,
          subjects: [],
          stream: ''
        };
      }
      return {
        ...prev,
        subjects: newSubjects
      };
    });
  }, [isEleventhTwelfthClass, extractSubjectCode]);

  // Helper function to check if a field should be read-only
  const isFieldReadOnly = (fieldName: string): boolean => {
    // If isReadOnly is true (for school admin/teacher viewing), all fields are read-only
    if (isReadOnly) return true;

    // If teacher is editing a student (isEdit && isStudentEdit), disable studentName and dateOfBirth
    // (School Admin should be able to edit these fields)
    if (isEdit && isStudentEdit && isTeacher()) {
      if (fieldName === 'studentName' || fieldName === 'dateOfBirth') {
        return true;
      }
    }

    // If school admin/teacher is editing student details from student management
    // keep mobile number locked (not editable).
    if (isStudentEdit && !isStudentProfileUpdate && fieldName === 'mobileNo') {
      return true;
    }

    if (fieldName === 'subjects') {
      if (shouldLockSubjectsForMarksheets) return true;

      if (isStudentProfileUpdate) {

        const initialSubjects = initialData?.subjects ? normalizeSubjects(initialData.subjects) : [];
        const initialSubjectsCount = initialSubjects.length;
        return initialSubjectsCount >= 1;
      }
    }

    // If not a student profile update, fields are editable (except mobileNo handled above)
    if (!isStudentProfileUpdate) return false;

    // List of editable fields for student profile updates
    const editableFields = [
      'sssmid',
      'penNo',
      'apparId',
      'religion',
      'caste',
      'casteCertificate',
      'subCaste',
      'aadharNo',
      'studentPhoto', // Student can update/add photo
      'houseNo',
      'street',
      'landmark',
      'area',
      'city',
      'postOffice',
      'district',
      'state',
      'pinCode',
      'gmail',
      'bankName',
      'ifcCode',
      'accountNo',
      'annualIncome',
      'cwsnCode',
      'sambalKramank',
      'house'
    ];

    return !editableFields.includes(fieldName);
  };

  const canSchoolAdminEditSection = isEdit && isSchoolAdmin() && !isReadOnly;
  const sectionOptions = React.useMemo(() => {
    const allClasses = classesQuery.data || [];
    const targetClassName = (selectedClass?.name || formData.admissionClass || '').trim().toLowerCase();

    if (!targetClassName) {
      return formData.section ? [formData.section] : [];
    }

    const uniqueSections = Array.from(
      new Set(
        allClasses
          .filter((cls: any) => (cls?.name || '').trim().toLowerCase() === targetClassName)
          .map((cls: any) => (cls?.section || '').trim())
          .filter(Boolean)
      )
    );

    if (formData.section && !uniqueSections.includes(formData.section)) {
      uniqueSections.push(formData.section);
    }

    return uniqueSections.sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  }, [classesQuery.data, selectedClass?.name, formData.admissionClass, formData.section]);

  // Helper function to check if a subject belongs to Language Group
  const isLanguageSubject = useCallback((subjectCode: string, subjectName: string): boolean => {
    if (!isEleventhTwelfthClass) return false;
    const languageGroup = ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED.find(g => g.groupName === "LANGUAGE");
    if (!languageGroup) return false;
    return languageGroup.subjects.some(s => s.code === subjectCode || s.name === subjectName);
  }, [isEleventhTwelfthClass]);

  // Helper function to check if a subject belongs to Additional Subject group
  const isAdditionalSubject = useCallback((subjectCode: string, subjectName: string): boolean => {
    if (!isEleventhTwelfthClass) return false;
    const additionalGroup = ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED.find(g => g.groupName === "ADDITIONAL SUBJECT (OPTIONAL)");
    if (!additionalGroup) return false;
    // Check ONLY by code, not by name, to avoid confusion with subjects that have same name but different codes
    return additionalGroup.subjects.some(s => s.code === subjectCode);
  }, [isEleventhTwelfthClass]);

  // Helper function to check if a subject belongs to NSQF Subject group
  const isNSQFSubject = useCallback((subjectCode: string, subjectName: string): boolean => {
    if (!isEleventhTwelfthClass) return false;
    const nsqfGroup = ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED.find(g => g.groupName === "NSQF SUBJECT (OPTIONAL)");
    if (!nsqfGroup) return false;
    // Check ONLY by code, not by name, to avoid confusion with subjects that have same name but different codes
    return nsqfGroup.subjects.some(s => s.code === subjectCode);
  }, [isEleventhTwelfthClass]);

  // Helper function to get main group name for a subject (excluding Language, Additional, NSQF)
  const getMainGroupName = useCallback((subjectCode: string, subjectName: string): string | null => {
    if (!isEleventhTwelfthClass) return null;

    // First, check if this subject belongs to the LANGUAGE group (first step)
    // If it does, it should NOT be considered as part of any main group
    if (isLanguageSubject(subjectCode, subjectName)) {
      return null;
    }

    const mainGroups = [
      "HUMANITIES GROUP",
      "SCIENCE MATHS GROUP",
      "COMMERCE GROUP",
      "AGRICULTURE GROUP",
      "FINE ART GROUP",
      "HOME SCIENCE GROUP",
      "SCIENCE BIO. GROUP",
      "LANGUAGE GROUP"
    ];

    for (const groupName of mainGroups) {
      const group = ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED.find(g => g.groupName === groupName);
      if (group) {
        // Check in subjects array
        if (group.subjects.some(s => s.code === subjectCode || s.name === subjectName)) {
          return groupName;
        }
        // Check in subgroups if they exist
        if (group.hasSubgroups && group.subgroups) {
          for (const subgroup of group.subgroups) {
            if (subgroup.subjects.some(s => s.code === subjectCode || s.name === subjectName)) {
              return groupName;
            }
          }
        }
      }
    }
    return null;
  }, [isEleventhTwelfthClass, isLanguageSubject]);

  // Initialize selectedMainGroup from existing formData when editing
  // Also convert old format (just code) to new format (GROUP|CODE) if needed
  // Expand all groups and subgroups that have selected subjects
  useEffect(() => {
    if (open && isEleventhTwelfthClass && formData.subjects.length > 0 && classSubjects) {
      // Convert old format to new format if needed and find main group
      // Smart categorization: First 2 languages go to LANGUAGE, additional languages go to LANGUAGE GROUP
      const languageSubjectCodes: string[] = [];
      const otherSubjects: string[] = [];

      // First pass: Separate subjects
      // Business rule: the FIRST TWO entries in subjects array are always initial LANGUAGE subjects.
      // Any additional languages beyond the first two should be treated as main group subjects
      // (typically in LANGUAGE GROUP Subgroup A), not as initial LANGUAGE selections.
      formData.subjects.forEach((storedValue: string, index: number) => {
        if (storedValue.includes('|')) {
          // Already in new format
          const [groupName, subjectCode] = storedValue.split('|');
          // Check if this is actually a language subject (regardless of current group)
          const subject = findSubjectByCode(subjectCode);
          const isLang = subject && isLanguageSubject(subjectCode, subject.name);

          if (index < 2 && isLang) {
            // First 2 entries (languages) go to LANGUAGE section
            languageSubjectCodes.push(subjectCode);
          } else {
            // Everything else (including additional languages) will be handled as main/optional
            otherSubjects.push(storedValue);
          }
        } else {
          // Old format - check if it's a language
          const subject = findSubjectByCode(storedValue);
          const isLang = subject && isLanguageSubject(storedValue, subject.name);

          if (index < 2 && isLang) {
            languageSubjectCodes.push(storedValue);
          } else {
            otherSubjects.push(storedValue);
          }
        }
      });

      // Second pass: Convert subjects with smart categorization
      const convertedSubjects: string[] = [];

      // Process language subjects: First 2 go to LANGUAGE only
      languageSubjectCodes.forEach((subjectCode, index) => {
        if (index < 2) {
          // First 2 languages go to LANGUAGE section
          convertedSubjects.push(`LANGUAGE|${subjectCode}`);
        }
      });

      // Check if we have a language in LANGUAGE GROUP Subgroup A
      // If yes, prioritize LANGUAGE GROUP for subjects that are in LANGUAGE GROUP Subgroup B
      const hasLanguageInLanguageGroup = convertedSubjects.some(s => {
        if (s.startsWith('LANGUAGE GROUP|')) {
          const code = s.split('|')[1];
          const subgroupACodes = ["051", "052", "053", "054", "055"];
          return subgroupACodes.includes(code);
        }
        return false;
      });

      // Process other subjects
      otherSubjects.forEach((storedValue: string) => {
        if (storedValue.includes('|')) {
          // Already in new format - check if it needs re-categorization
          const [currentGroup, subjectCode] = storedValue.split('|');

          // If we have a language in LANGUAGE GROUP and this subject is in LANGUAGE GROUP Subgroup B,
          // but it's currently categorized in another group, re-categorize it to LANGUAGE GROUP
          if (hasLanguageInLanguageGroup && currentGroup !== 'LANGUAGE GROUP') {
            const languageGroupDef = ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED.find(g => g.groupName === "LANGUAGE GROUP");
            if (languageGroupDef && languageGroupDef.hasSubgroups) {
              const subgroupB = languageGroupDef.subgroups.find(sg => sg.subgroupName === "B");
              if (subgroupB && subgroupB.subjects.some(s => s.code === subjectCode)) {
                // This subject is in LANGUAGE GROUP Subgroup B, re-categorize it
                convertedSubjects.push(`LANGUAGE GROUP|${subjectCode}`);
                return;
              }
            }
          }

          // Keep as is
          convertedSubjects.push(storedValue);
        } else {
          // Old format - convert to new format
          const subjectCode = storedValue;
          const subject = findSubjectByCode(subjectCode);
          if (!subject) {
            convertedSubjects.push(storedValue);
            return;
          }

          // Determine which group this subject belongs to
          // Special case: If this is a language (but NOT in first two positions),
          // treat it as part of LANGUAGE GROUP (Subgroup A) instead of initial LANGUAGE.
          if (subject && isLanguageSubject(subjectCode, subject.name)) {
            convertedSubjects.push(`LANGUAGE GROUP|${subjectCode}`);
          } else if (isAdditionalSubject(subjectCode, subject.name)) {
            convertedSubjects.push(`ADDITIONAL SUBJECT (OPTIONAL)|${subjectCode}`);
          } else if (isNSQFSubject(subjectCode, subject.name)) {
            convertedSubjects.push(`NSQF SUBJECT (OPTIONAL)|${subjectCode}`);
          } else {
            // If we have a language in LANGUAGE GROUP and this subject is in LANGUAGE GROUP Subgroup B,
            // prioritize LANGUAGE GROUP over other groups
            if (hasLanguageInLanguageGroup) {
              const languageGroupDef = ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED.find(g => g.groupName === "LANGUAGE GROUP");
              if (languageGroupDef && languageGroupDef.hasSubgroups) {
                const subgroupB = languageGroupDef.subgroups.find(sg => sg.subgroupName === "B");
                if (subgroupB && subgroupB.subjects.some(s => s.code === subjectCode || s.name === subject.name)) {
                  // This subject is in LANGUAGE GROUP Subgroup B, prioritize it
                  convertedSubjects.push(`LANGUAGE GROUP|${subjectCode}`);
                  return;
                }
              }
            }

            const mainGroupName = getMainGroupName(subjectCode, subject.name);
            if (mainGroupName) {
              convertedSubjects.push(`${mainGroupName}|${subjectCode}`);
            } else {
              convertedSubjects.push(storedValue);
            }
          }
        }
      });

      // Update formData if conversion was needed or if we need to re-categorize languages
      const needsConversion = formData.subjects.some((s: string) => !s.includes('|'));
      const needsRecategorization = (() => {
        // Check if there are more than 2 languages in LANGUAGE section
        const languageSubjects = formData.subjects.filter(s => s.startsWith('LANGUAGE|') && !s.startsWith('LANGUAGE GROUP|'));
        if (languageSubjects.length > 2) return true;

        // Check if we have a language in LANGUAGE GROUP Subgroup A
        const hasLanguageInLanguageGroup = formData.subjects.some(s => {
          if (s.startsWith('LANGUAGE GROUP|')) {
            const code = s.split('|')[1];
            const subgroupACodes = ["051", "052", "053", "054", "055"];
            return subgroupACodes.includes(code);
          }
          return false;
        });

        // If we have a language in LANGUAGE GROUP, check if subjects that should be in LANGUAGE GROUP Subgroup B
        // are incorrectly categorized in other groups (like HUMANITIES GROUP)
        if (hasLanguageInLanguageGroup) {
          const languageGroupDef = ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED.find(g => g.groupName === "LANGUAGE GROUP");
          if (languageGroupDef && languageGroupDef.hasSubgroups) {
            const subgroupB = languageGroupDef.subgroups.find(sg => sg.subgroupName === "B");
            if (subgroupB) {
              const subgroupBCodes = subgroupB.subjects.map(s => s.code);
              // Check if any subject in LANGUAGE GROUP Subgroup B is categorized in another group
              const needsRecat = formData.subjects.some(s => {
                if (s.includes('|')) {
                  const [groupName, subjectCode] = s.split('|');
                  // If subject is in LANGUAGE GROUP Subgroup B but categorized in another main group
                  if (subgroupBCodes.includes(subjectCode) && groupName !== 'LANGUAGE GROUP') {
                    return true;
                  }
                }
                return false;
              });
              if (needsRecat) return true;
            }
          }
        }

        return false;
      })();

      if (needsConversion || needsRecategorization) {
        setFormData(prev => ({ ...prev, subjects: convertedSubjects }));
      }

      // When editing, expand all groups and subgroups that have selected subjects
      if (isEdit) {
        const groupsToExpand = new Set<string>();
        const subgroupsToExpand = new Set<string>();
        let foundMainGroup: string | null = null;

        // Process all converted subjects to find groups and subgroups
        for (const storedValue of convertedSubjects) {
          if (storedValue.includes('|')) {
            const [groupName, subjectCode] = storedValue.split('|');

            // Check if it's a language subject
            if (groupName === 'LANGUAGE') {
              setLanguageGroupExpanded(true);
            }
            // Check if it's a main group
            else {
              const mainGroups = [
                "HUMANITIES GROUP",
                "SCIENCE MATHS GROUP",
                "COMMERCE GROUP",
                "AGRICULTURE GROUP",
                "FINE ART GROUP",
                "HOME SCIENCE GROUP",
                "SCIENCE BIO. GROUP",
                "LANGUAGE GROUP"
              ];

              if (mainGroups.includes(groupName)) {
                groupsToExpand.add(groupName);
                if (!foundMainGroup) {
                  foundMainGroup = groupName;
                }

                // Check if this group has subgroups and find which subgroup this subject belongs to
                const groupDef = ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED.find(g => g.groupName === groupName);
                if (groupDef && groupDef.hasSubgroups && groupDef.subgroups) {
                  for (const subgroup of groupDef.subgroups) {
                    const subjectInSubgroup = subgroup.subjects.find((s: any) => s.code === subjectCode);
                    if (subjectInSubgroup) {
                      subgroupsToExpand.add(`${groupName}-${subgroup.subgroupName}`);
                      break;
                    }
                  }
                }
              }
              // Check if it's Additional or NSQF group
              else if (groupName === 'ADDITIONAL SUBJECT (OPTIONAL)' || groupName === 'NSQF SUBJECT (OPTIONAL)') {
                groupsToExpand.add(groupName);
              }
            }
          }
        }

        // Set the selected main group (use the first one found)
        if (foundMainGroup && !selectedMainGroup) {
          setSelectedMainGroup(foundMainGroup);
          // Also set the stream based on the found main group
          const streamOption = streamOptions.find(opt => opt.groupName === foundMainGroup);
          if (streamOption && !selectedStream) {
            setSelectedStream(streamOption.value);
          }
        }

        // Expand all groups that have selected subjects
        setExpandedGroups(prev => {
          const newSet = new Set(prev);
          groupsToExpand.forEach(group => newSet.add(group));
          return newSet;
        });

        // Expand all subgroups that have selected subjects
        setExpandedSubgroups(prev => {
          const newSet = new Set(prev);
          subgroupsToExpand.forEach(subgroup => newSet.add(subgroup));
          return newSet;
        });
      } else {
        // For new forms, find the first main group (if any) but don't expand everything
        for (const storedValue of convertedSubjects) {
          if (storedValue.includes('|')) {
            const groupName = storedValue.split('|')[0];
            const mainGroups = [
              "HUMANITIES GROUP",
              "SCIENCE MATHS GROUP",
              "COMMERCE GROUP",
              "AGRICULTURE GROUP",
              "FINE ART GROUP",
              "HOME SCIENCE GROUP",
              "SCIENCE BIO. GROUP",
              "LANGUAGE GROUP"
            ];
            if (mainGroups.includes(groupName) && !selectedMainGroup) {
              setSelectedMainGroup(groupName);
              // Also set the stream based on the found main group
              const streamOption = streamOptions.find(opt => opt.groupName === groupName);
              if (streamOption && !selectedStream) {
                setSelectedStream(streamOption.value);
              }
              setExpandedGroups(prev => {
                const newSet = new Set(prev);
                newSet.add(groupName);
                return newSet;
              });
              break; // Only need to find one
            }
          }
        }
      }
    } else if (open && !isEdit) {
      // Reset for new forms
      setSelectedMainGroup(null);
      setSelectedStream(formData.stream || ''); // Initialize from formData if available
      setExpandedGroups(new Set());
      setExpandedSubgroups(new Set());
      setLanguageGroupExpanded(true);
    }

    // Initialize stream from formData when editing
    if (open && isEdit && formData.stream && !selectedStream) {
      // Check if formData.stream is a group name (new format) or code (old format)
      const streamOptionByGroupName = streamOptions.find(opt => opt.groupName === formData.stream);
      const streamOptionByCode = streamOptions.find(opt => opt.value === formData.stream);

      if (streamOptionByGroupName) {
        // New format: stream contains group name
        setSelectedStream(streamOptionByGroupName.value); // Set code for dropdown
        if (!selectedMainGroup) {
          setSelectedMainGroup(streamOptionByGroupName.groupName);
        }
      } else if (streamOptionByCode) {
        // Old format: stream contains code, convert to group name
        setSelectedStream(streamOptionByCode.value); // Set code for dropdown
        setFormData(prev => ({ ...prev, stream: streamOptionByCode.groupName })); // Update to group name
        if (!selectedMainGroup) {
          setSelectedMainGroup(streamOptionByCode.groupName);
        }
      }
    }
  }, [open, formData.subjects, formData.stream, classSubjects, isEleventhTwelfthClass, isEdit, getMainGroupName, selectedMainGroup, selectedStream, isLanguageSubject, isAdditionalSubject, isNSQFSubject, getBackendSubjectCode]);

  // Get selected language subjects (only from LANGUAGE section, not LANGUAGE GROUP)
  // This should only return the first 2 languages that are in the LANGUAGE section
  const getSelectedLanguageSubjects = useCallback((): string[] => {
    return formData.subjects.filter(storedValue => {
      // Only count subjects that are explicitly in the LANGUAGE section (not LANGUAGE GROUP)
      return storedValue.startsWith('LANGUAGE|') && !storedValue.startsWith('LANGUAGE GROUP|');
    });
  }, [formData.subjects]);

  // Clear stream if less than 2 languages are selected
  useEffect(() => {
    if (isEleventhTwelfthClass && open) {
      const selectedLanguages = getSelectedLanguageSubjects();
      if (selectedLanguages.length < 2 && selectedStream) {
        // Clear stream if less than 2 languages are selected
        setSelectedStream('');
        setFormData(prev => ({ ...prev, stream: '' }));
        setSelectedMainGroup(null);
      }
    }
  }, [formData.subjects, isEleventhTwelfthClass, open, selectedStream, getSelectedLanguageSubjects]);

  // Function to sort subjects in the required order:
  // 1. First two languages (from initial LANGUAGE section)
  // 2. Main group subjects (Subgroup A first, then Subgroup B)
  // 3. Optional subjects
  const getSortedSubjects = useCallback((): string[] => {
    if (!isEleventhTwelfthClass) {
      return formData.subjects; // For non-11th/12th classes, return as is
    }

    const sortedSubjects: string[] = [];

    // 1. First two languages (from initial LANGUAGE section)
    const initialLanguages = formData.subjects.filter(storedValue =>
      storedValue.startsWith('LANGUAGE|') && !storedValue.startsWith('LANGUAGE GROUP|')
    );
    sortedSubjects.push(...initialLanguages);

    // 2. Main group subjects (organized by group, then by subgroup)
    const mainGroupSubjects = formData.subjects.filter(storedValue => {
      if (storedValue.startsWith('LANGUAGE|') && !storedValue.startsWith('LANGUAGE GROUP|')) {
        return false; // Already added as initial languages
      }
      if (storedValue.startsWith('ADDITIONAL SUBJECT (OPTIONAL)|') ||
        storedValue.startsWith('NSQF SUBJECT (OPTIONAL)|')) {
        return false; // Will be added as optional
      }
      return true; // Main group subject
    });

    // Group main group subjects by group name
    const subjectsByGroup: { [key: string]: { subgroupA: string[], subgroupB: string[], other: string[] } } = {};

    mainGroupSubjects.forEach(storedValue => {
      const code = extractSubjectCode(storedValue);
      const groupName = storedValue.includes('|') ? storedValue.split('|')[0] : '';

      if (!subjectsByGroup[groupName]) {
        subjectsByGroup[groupName] = { subgroupA: [], subgroupB: [], other: [] };
      }

      // Determine if it's Subgroup A or B based on subject codes
      // For LANGUAGE GROUP
      if (groupName === 'LANGUAGE GROUP') {
        const subgroupACodes = ["051", "052", "053", "054", "055"];
        const subgroupBCodes = ["110", "120", "130", "140", "166"];
        if (subgroupACodes.includes(code)) {
          subjectsByGroup[groupName].subgroupA.push(storedValue);
        } else if (subgroupBCodes.includes(code)) {
          subjectsByGroup[groupName].subgroupB.push(storedValue);
        } else {
          subjectsByGroup[groupName].other.push(storedValue);
        }
      }
      // For HUMANITIES GROUP
      else if (groupName === 'HUMANITIES GROUP') {
        const subgroupACodes = ["110", "120", "130", "140"];
        const subgroupBCodes = ["150", "151", "162", "163", "164", "165", "166", "167", "168"];
        if (subgroupACodes.includes(code)) {
          subjectsByGroup[groupName].subgroupA.push(storedValue);
        } else if (subgroupBCodes.includes(code)) {
          subjectsByGroup[groupName].subgroupB.push(storedValue);
        } else {
          subjectsByGroup[groupName].other.push(storedValue);
        }
      }
      // For COMMERCE GROUP
      else if (groupName === 'COMMERCE GROUP') {
        const subgroupACodes = ["150", "151"];
        const subgroupBCodes = ["140"];
        if (subgroupACodes.includes(code)) {
          subjectsByGroup[groupName].subgroupA.push(storedValue);
        } else if (subgroupBCodes.includes(code)) {
          subjectsByGroup[groupName].subgroupB.push(storedValue);
        } else {
          subjectsByGroup[groupName].other.push(storedValue);
        }
      }
      // For other groups, add to other
      else {
        subjectsByGroup[groupName].other.push(storedValue);
      }
    });

    // Add main group subjects in order: Subgroup A, then Subgroup B, then other
    Object.keys(subjectsByGroup).sort().forEach(groupName => {
      const group = subjectsByGroup[groupName];
      sortedSubjects.push(...group.subgroupA);
      sortedSubjects.push(...group.subgroupB);
      sortedSubjects.push(...group.other);
    });

    // 3. Optional subjects
    const optionalSubjects = formData.subjects.filter(storedValue =>
      storedValue.startsWith('ADDITIONAL SUBJECT (OPTIONAL)|') ||
      storedValue.startsWith('NSQF SUBJECT (OPTIONAL)|')
    );
    sortedSubjects.push(...optionalSubjects);

    return sortedSubjects;
  }, [formData.subjects, isEleventhTwelfthClass, extractSubjectCode]);

  // Get selected main group subjects
  const getSelectedMainGroupSubjects = useCallback((): string[] => {
    if (!selectedMainGroup) return [];
    return formData.subjects.filter(storedValue => {
      // Check if the stored value starts with the selected group name
      return storedValue.startsWith(`${selectedMainGroup}|`);
    });
  }, [formData.subjects, selectedMainGroup]);

  // Get all main group subjects (from any main group, not just selected one)
  const getAllMainGroupSubjects = useCallback((): string[] => {
    const mainGroups = [
      "HUMANITIES GROUP",
      "SCIENCE MATHS GROUP",
      "COMMERCE GROUP",
      "AGRICULTURE GROUP",
      "FINE ART GROUP",
      "HOME SCIENCE GROUP",
      "SCIENCE BIO. GROUP",
      "LANGUAGE GROUP"
    ];
    return formData.subjects.filter(storedValue => {
      // Check if stored value starts with any main group name
      return mainGroups.some(group => storedValue.startsWith(`${group}|`));
    });
  }, [formData.subjects]);

  // Get names of subjects selected in any main group (for distinguishing from optional groups)
  const getSelectedMainGroupSubjectNames = useCallback((): Set<string> => {
    const names = new Set<string>();
    if (!classSubjects) return names;

    const mainGroupSubjects = getAllMainGroupSubjects();
    mainGroupSubjects.forEach(storedValue => {
      const code = getBackendSubjectCode(storedValue);
      const subject = findSubjectByCode(code);
      if (subject?.name) {
        names.add(subject.name);
      }
    });

    return names;
  }, [classSubjects, getAllMainGroupSubjects, getBackendSubjectCode, findSubjectByCode]);

  // Get selected additional/NSQF subject
  const getSelectedOptionalSubject = useCallback((): string | null => {
    const optionalSubject = formData.subjects.find(storedValue => {
      return storedValue.startsWith('ADDITIONAL SUBJECT (OPTIONAL)|') ||
        storedValue.startsWith('NSQF SUBJECT (OPTIONAL)|');
    });
    return optionalSubject || null;
  }, [formData.subjects]);

  // Get all selected subject codes (for filtering Additional/NSQF)
  const getSelectedSubjectCodes = useCallback((): Set<string> => {
    const codes = new Set<string>();
    formData.subjects.forEach(storedValue => {
      const code = extractSubjectCode(storedValue);
      codes.add(code);
    });
    return codes;
  }, [formData.subjects, extractSubjectCode]);

  // Function to handle language subject selection (max 2, max 6 total)
  const handleToggleLanguageSubject = useCallback((subjectValue: string) => {
    if (isFieldReadOnly('subjects')) return;

    setFormData(prev => {
      const isSelected = prev.subjects.includes(subjectValue);
      // Count language subjects (those starting with "LANGUAGE|")
      const selectedLanguages = prev.subjects.filter(storedValue =>
        storedValue.startsWith('LANGUAGE|')
      );

      if (isSelected) {
        // If deselecting a language, remove ALL main group and optional subjects
        // This ensures that when languages are changed, the entire subject selection is reset
        const mainGroups = [
          "HUMANITIES GROUP",
          "SCIENCE MATHS GROUP",
          "COMMERCE GROUP",
          "AGRICULTURE GROUP",
          "FINE ART GROUP",
          "HOME SCIENCE GROUP",
          "SCIENCE BIO. GROUP",
          "LANGUAGE GROUP"
        ];

        // Remove all main group subjects
        const subjectsWithoutMainGroups = prev.subjects.filter(storedValue => {
          const isMainGroup = mainGroups.some(group => storedValue.startsWith(`${group}|`));
          return !isMainGroup;
        });

        // Remove all optional subjects (Additional and NSQF)
        const subjectsWithoutOptional = subjectsWithoutMainGroups.filter(storedValue => {
          return !storedValue.startsWith('ADDITIONAL SUBJECT (OPTIONAL)|') &&
            !storedValue.startsWith('NSQF SUBJECT (OPTIONAL)|');
        });

        // Remove the deselected language
        const finalSubjects = subjectsWithoutOptional.filter(code => code !== subjectValue);

        // Clear main group selection and stream if all subjects are removed
        if (finalSubjects.length === 0) {
          setSelectedMainGroup(null);
          setSelectedStream('');
        } else {
          // Check if any main group subjects remain
          const hasMainGroupSubjects = finalSubjects.some(storedValue => {
            return mainGroups.some(group => storedValue.startsWith(`${group}|`));
          });
          if (!hasMainGroupSubjects) {
            setSelectedMainGroup(null);
            setSelectedStream('');
          }
        }

        return {
          ...prev,
          subjects: finalSubjects,
          stream: '' // Clear stream when languages are deselected
        };
      } else {
        // Check if maximum limit (2 languages) is reached
        if (selectedLanguages.length >= 2) {
          return prev;
        }
        // Check if total subject limit (6) is reached
        if (prev.subjects.length >= 6) {
          return prev;
        }
        // Add the subject
        return {
          ...prev,
          subjects: [...prev.subjects, subjectValue]
        };
      }
    });
  }, [isFieldReadOnly]);

  // Function to handle main group selection
  const handleToggleMainGroup = useCallback((groupName: string) => {
    if (isFieldReadOnly('subjects')) return;

    if (selectedMainGroup === groupName) {
      // Deselect the group - remove all subjects from this group and collapse
      setFormData(prev => {
        const subjectsToRemove = prev.subjects.filter(storedValue =>
          storedValue.startsWith(`${groupName}|`)
        );
        const newSubjects = prev.subjects.filter(code => !subjectsToRemove.includes(code));
        // If all subjects are removed, clear stream
        if (newSubjects.length === 0) {
          setSelectedStream('');
          return {
            ...prev,
            subjects: [],
            stream: ''
          };
        }
        return {
          ...prev,
          subjects: newSubjects
        };
      });
      setSelectedMainGroup(null);
      setExpandedGroups(prev => {
        const newSet = new Set(prev);
        newSet.delete(groupName);
        return newSet;
      });
    } else {
      // Select new group - remove subjects from previous group if any
      if (selectedMainGroup) {
        setFormData(prev => {
          const subjectsToRemove = prev.subjects.filter(storedValue =>
            storedValue.startsWith(`${selectedMainGroup}|`)
          );
          return {
            ...prev,
            subjects: prev.subjects.filter(code => !subjectsToRemove.includes(code))
          };
        });
        setExpandedGroups(prev => {
          const newSet = new Set(prev);
          newSet.delete(selectedMainGroup);
          return newSet;
        });
      }
      setSelectedMainGroup(groupName);
      setExpandedGroups(prev => {
        const newSet = new Set(prev);
        newSet.add(groupName);
        return newSet;
      });
    }
  }, [selectedMainGroup, isFieldReadOnly]);

  // Function to handle main group subject selection (max 3 per group, max 6 total)
  const handleToggleMainGroupSubject = useCallback((subjectValue: string) => {
    if (isFieldReadOnly('subjects') || !selectedMainGroup) return;

    setFormData(prev => {
      // Extract group name from subjectValue (format: "GROUP|CODE")
      const groupName = subjectValue.split('|')[0];
      if (!groupName) return prev;

      // Extract subject code
      const subjectCode = extractSubjectCode(subjectValue);

      // Check if subject is selected by code (not just exact string match)
      // This handles cases where subject might be stored with different group prefix
      const isSelected = prev.subjects.some(storedValue => {
        const storedCode = extractSubjectCode(storedValue);
        return storedCode === subjectCode;
      });

      // Find the actual stored value (might have different prefix)
      const actualStoredValue = prev.subjects.find(storedValue => {
        const storedCode = extractSubjectCode(storedValue);
        return storedCode === subjectCode;
      });

      // Get all subjects from this main group
      const subjectsInThisGroup = prev.subjects.filter(storedValue =>
        storedValue.startsWith(`${groupName}|`)
      );

      // Special validation for HUMANITIES GROUP
      if (groupName === "HUMANITIES GROUP") {
        const subgroupACodes = ["110", "120", "130", "140"]; // History, Geography, Political Science, Economics
        const subgroupBCodes = ["150", "151", "162", "163", "164", "165", "166", "167", "168"];
        const isSubgroupA = subgroupACodes.includes(subjectCode);
        const isSubgroupB = subgroupBCodes.includes(subjectCode);

        // Get current counts
        const subgroupASubjects = subjectsInThisGroup.filter(storedValue => {
          const code = extractSubjectCode(storedValue);
          return subgroupACodes.includes(code);
        });
        const subgroupBSubjects = subjectsInThisGroup.filter(storedValue => {
          const code = extractSubjectCode(storedValue);
          return subgroupBCodes.includes(code);
        });

        const subgroupACount = subgroupASubjects.length;
        const subgroupBCount = subgroupBSubjects.length;

        if (isSelected && actualStoredValue) {
          // Removing a subject - use the actual stored value, not the subjectValue
          let updatedSubjects = prev.subjects.filter(storedValue => storedValue !== actualStoredValue);

          // After removal, recalculate counts
          const updatedGroupSubjects = updatedSubjects.filter(storedValue =>
            storedValue.startsWith(`${groupName}|`)
          );
          const updatedSubgroupASubjects = updatedGroupSubjects.filter(storedValue => {
            const code = extractSubjectCode(storedValue);
            return subgroupACodes.includes(code);
          });
          const updatedSubgroupBSubjects = updatedGroupSubjects.filter(storedValue => {
            const code = extractSubjectCode(storedValue);
            return subgroupBCodes.includes(code);
          });
          const updatedSubgroupACount = updatedSubgroupASubjects.length;
          const updatedSubgroupBCount = updatedSubgroupBSubjects.length;

          // If removing from Subgroup A and we now have less than 2, remove all from Subgroup B
          if (isSubgroupA && updatedSubgroupACount < 2) {
            updatedSubjects = updatedSubjects.filter(storedValue => {
              if (storedValue.startsWith("HUMANITIES GROUP|")) {
                const code = extractSubjectCode(storedValue);
                return !subgroupBCodes.includes(code);
              }
              return true;
            });
          }
          // If removing from Subgroup B and we now have 2 from Subgroup A, ensure we have exactly 1 from Subgroup B
          // (This is handled by the UI disabling/enabling, but we can clean up if needed)

          return {
            ...prev,
            subjects: updatedSubjects
          };
        } else {
          // Adding a subject

          // If trying to add to Subgroup A
          if (isSubgroupA) {
            // Can add up to 3 subjects from Subgroup A
            if (subgroupACount >= 3) {
              return prev; // Already at max
            }
            // If adding 3rd subject to Subgroup A, remove all from Subgroup B
            if (subgroupACount === 2) {
              // Will have 3 from A after adding, so remove all from B
              const updatedSubjects = prev.subjects.filter(storedValue => {
                if (storedValue.startsWith("HUMANITIES GROUP|")) {
                  const code = extractSubjectCode(storedValue);
                  return !subgroupBCodes.includes(code);
                }
                return true;
              });
              // Check total limit
              if (updatedSubjects.length >= 6) {
                return prev;
              }
              return {
                ...prev,
                subjects: [...updatedSubjects, subjectValue]
              };
            }
          }

          // If trying to add to Subgroup B
          if (isSubgroupB) {
            // Can only add to Subgroup B if we have exactly 2 from Subgroup A
            if (subgroupACount !== 2) {
              return prev; // Must have exactly 2 from A to add to B
            }
            // Can only have 1 subject from Subgroup B
            if (subgroupBCount >= 1) {
              return prev; // Already have 1 from B
            }
          }

          // Check if total subject limit (6) is reached
          if (prev.subjects.length >= 6) {
            return prev;
          }

          // Add the subject
          return {
            ...prev,
            subjects: [...prev.subjects, subjectValue]
          };
        }
      }

      // Special validation for COMMERCE GROUP
      if (groupName === "COMMERCE GROUP") {
        const subgroupACodes = ["310", "320"]; // Business Studies, Book Keeping & Accountancy (both required)
        const subgroupBCodes = ["150", "151", "140"]; // Mathematics, Informatic Practices, Economics (exactly one required)
        const isSubgroupA = subgroupACodes.includes(subjectCode);
        const isSubgroupB = subgroupBCodes.includes(subjectCode);

        // Get current counts
        const subgroupASubjects = subjectsInThisGroup.filter(storedValue => {
          const code = extractSubjectCode(storedValue);
          return subgroupACodes.includes(code);
        });
        const subgroupBSubjects = subjectsInThisGroup.filter(storedValue => {
          const code = extractSubjectCode(storedValue);
          return subgroupBCodes.includes(code);
        });

        const subgroupACount = subgroupASubjects.length;
        const subgroupBCount = subgroupBSubjects.length;

        if (isSelected && actualStoredValue) {
          // Removing a subject - use the actual stored value, not the subjectValue
          let updatedSubjects = prev.subjects.filter(storedValue => storedValue !== actualStoredValue);

          // After removal, recalculate counts
          const updatedGroupSubjects = updatedSubjects.filter(storedValue =>
            storedValue.startsWith(`${groupName}|`)
          );
          const updatedSubgroupASubjects = updatedGroupSubjects.filter(storedValue => {
            const code = extractSubjectCode(storedValue);
            return subgroupACodes.includes(code);
          });
          const updatedSubgroupBSubjects = updatedGroupSubjects.filter(storedValue => {
            const code = extractSubjectCode(storedValue);
            return subgroupBCodes.includes(code);
          });
          const updatedSubgroupACount = updatedSubgroupASubjects.length;
          const updatedSubgroupBCount = updatedSubgroupBSubjects.length;

          // If removing from Subgroup A and we now have less than 2, remove all from Subgroup B
          if (isSubgroupA && updatedSubgroupACount < 2) {
            updatedSubjects = updatedSubjects.filter(storedValue => {
              if (storedValue.startsWith("COMMERCE GROUP|")) {
                const code = extractSubjectCode(storedValue);
                return !subgroupBCodes.includes(code);
              }
              return true;
            });
          }

          return {
            ...prev,
            subjects: updatedSubjects
          };
        } else {
          // Adding a subject

          // If trying to add to Subgroup A
          if (isSubgroupA) {
            // Can only add up to 2 subjects from Subgroup A (both are required)
            if (subgroupACount >= 2) {
              return prev; // Already have both from A
            }
          }

          // If trying to add to Subgroup B
          if (isSubgroupB) {
            // Can only add to Subgroup B if we have exactly 2 from Subgroup A
            if (subgroupACount !== 2) {
              return prev; // Must have both from A to add to B
            }
            // Can only have 1 subject from Subgroup B
            if (subgroupBCount >= 1) {
              return prev; // Already have 1 from B
            }
          }

          // Check if total subject limit (6) is reached
          if (prev.subjects.length >= 6) {
            return prev;
          }

          // Add the subject
          return {
            ...prev,
            subjects: [...prev.subjects, subjectValue]
          };
        }
      }

      // Special validation for LANGUAGE GROUP
      if (groupName === "LANGUAGE GROUP") {
        const subgroupACodes = ["051", "052", "053", "054", "055"]; // Hindi, English, Sanskrit, Marathi, Urdu (exactly one required)
        const subgroupBCodes = ["110", "120", "130", "140", "166"]; // History, Geography, Political Science, Economics, Sociology (exactly two required)
        const isSubgroupA = subgroupACodes.includes(subjectCode);
        const isSubgroupB = subgroupBCodes.includes(subjectCode);

        // Get current counts
        const subgroupASubjects = subjectsInThisGroup.filter(storedValue => {
          const code = extractSubjectCode(storedValue);
          return subgroupACodes.includes(code);
        });
        const subgroupBSubjects = subjectsInThisGroup.filter(storedValue => {
          const code = extractSubjectCode(storedValue);
          return subgroupBCodes.includes(code);
        });

        const subgroupACount = subgroupASubjects.length;
        const subgroupBCount = subgroupBSubjects.length;

        if (isSelected && actualStoredValue) {
          // Removing a subject - use the actual stored value, not the subjectValue
          let updatedSubjects = prev.subjects.filter(storedValue => storedValue !== actualStoredValue);

          // After removal, recalculate counts
          const updatedGroupSubjects = updatedSubjects.filter(storedValue =>
            storedValue.startsWith(`${groupName}|`)
          );
          const updatedSubgroupASubjects = updatedGroupSubjects.filter(storedValue => {
            const code = extractSubjectCode(storedValue);
            return subgroupACodes.includes(code);
          });
          const updatedSubgroupBSubjects = updatedGroupSubjects.filter(storedValue => {
            const code = extractSubjectCode(storedValue);
            return subgroupBCodes.includes(code);
          });
          const updatedSubgroupACount = updatedSubgroupASubjects.length;
          const updatedSubgroupBCount = updatedSubgroupBSubjects.length;

          // If removing from Subgroup A and we now have 0, remove all from Subgroup B
          if (isSubgroupA && updatedSubgroupACount === 0) {
            updatedSubjects = updatedSubjects.filter(storedValue => {
              if (storedValue.startsWith("LANGUAGE GROUP|")) {
                const code = extractSubjectCode(storedValue);
                return !subgroupBCodes.includes(code);
              }
              return true;
            });
          }

          return {
            ...prev,
            subjects: updatedSubjects
          };
        } else {
          // Adding a subject
          // First, remove any existing subject with the same code (might have different prefix)
          let updatedSubjects = prev.subjects;
          if (actualStoredValue) {
            // Subject already exists with different prefix, remove it first
            updatedSubjects = prev.subjects.filter(storedValue => storedValue !== actualStoredValue);
          }

          // If trying to add to Subgroup A
          if (isSubgroupA) {
            // If there's already one selected, replace it
            if (subgroupACount >= 1) {
              // Remove the existing Subgroup A subject
              updatedSubjects = updatedSubjects.filter(storedValue => {
                if (storedValue.startsWith("LANGUAGE GROUP|")) {
                  const code = extractSubjectCode(storedValue);
                  return !subgroupACodes.includes(code);
                }
                return true;
              });

              // Re-categorize Subgroup B subjects to ensure they have "LANGUAGE GROUP|" prefix
              // This handles cases where Subgroup B subjects might have been stored with different prefixes
              updatedSubjects = updatedSubjects.map(storedValue => {
                const code = extractSubjectCode(storedValue);
                if (subgroupBCodes.includes(code) && !storedValue.startsWith("LANGUAGE GROUP|")) {
                  // Re-categorize to LANGUAGE GROUP
                  return `LANGUAGE GROUP|${code}`;
                }
                return storedValue;
              });

              // Check if total subject limit (6) is reached after replacement
              if (updatedSubjects.length >= 6) {
                return prev;
              }
              return {
                ...prev,
                subjects: [...updatedSubjects, subjectValue]
              };
            }
          }

          // If trying to add to Subgroup B
          if (isSubgroupB) {
            // Can only add to Subgroup B if we have exactly 1 from Subgroup A
            if (subgroupACount !== 1) {
              return prev; // Must have exactly 1 from A to add to B
            }
            // Can only have up to 2 subjects from Subgroup B
            if (subgroupBCount >= 2) {
              return prev; // Already have 2 from B
            }
          }

          // Check if total subject limit (6) is reached
          if (updatedSubjects.length >= 6) {
            return prev;
          }

          // Add the subject
          return {
            ...prev,
            subjects: [...updatedSubjects, subjectValue]
          };
        }
      }

      // For other groups, use the original logic
      if (isSelected && actualStoredValue) {
        // Remove the subject - use the actual stored value, not the subjectValue
        return {
          ...prev,
          subjects: prev.subjects.filter(storedValue => storedValue !== actualStoredValue)
        };
      } else {
        // Check if maximum limit (3 subjects) is reached for this group
        if (subjectsInThisGroup.length >= 3) {
          return prev;
        }
        // Check if total subject limit (6) is reached
        if (prev.subjects.length >= 6) {
          return prev;
        }
        // Add the subject
        return {
          ...prev,
          subjects: [...prev.subjects, subjectValue]
        };
      }
    });
  }, [selectedMainGroup, isFieldReadOnly, extractSubjectCode]);

  // Function to handle additional/NSQF subject selection (only one from either, max 6 total)
  const handleToggleOptionalSubject = useCallback((subjectValue: string) => {
    if (isFieldReadOnly('subjects')) return;

    setFormData(prev => {
      const isSelected = prev.subjects.includes(subjectValue);
      // Find any selected optional subject (Additional or NSQF)
      const selectedOptional = prev.subjects.find(storedValue =>
        storedValue.startsWith('ADDITIONAL SUBJECT (OPTIONAL)|') ||
        storedValue.startsWith('NSQF SUBJECT (OPTIONAL)|')
      );

      if (isSelected) {
        // Remove the subject
        return {
          ...prev,
          subjects: prev.subjects.filter(code => code !== subjectValue)
        };
      } else {
        // If another optional subject is selected, remove it first
        if (selectedOptional) {
          return {
            ...prev,
            subjects: prev.subjects.filter(code => code !== selectedOptional).concat(subjectValue)
          };
        }
        // Check if total subject limit (6) is reached
        if (prev.subjects.length >= 6) {
          return prev;
        }
        // Add the subject
        return {
          ...prev,
          subjects: [...prev.subjects, subjectValue]
        };
      }
    });
  }, [isFieldReadOnly]);

  // Function to toggle subject selection (add if not selected, remove if selected)
  // This is the main handler that routes to appropriate handler based on subject type
  const handleToggleSubject = useCallback((subjectValue: string) => {
    if (isFieldReadOnly('subjects')) return;

    // subjectValue is now in format "GROUP|CODE", extract the group name
    if (subjectValue.includes('|')) {
      const groupName = subjectValue.split('|')[0];

      if (groupName === 'LANGUAGE') {
        handleToggleLanguageSubject(subjectValue);
      } else if (groupName === 'ADDITIONAL SUBJECT (OPTIONAL)') {
        handleToggleOptionalSubject(subjectValue);
      } else if (groupName === 'NSQF SUBJECT (OPTIONAL)') {
        handleToggleOptionalSubject(subjectValue);
      } else {
        // Main group subject
        handleToggleMainGroupSubject(subjectValue);
      }
    } else {
      // Fallback for old format (shouldn't happen, but handle gracefully)
      const subjectCode = subjectValue;
      const subject = classSubjects?.find((cs: any) => cs.subjectCode === subjectCode);
      if (!subject) return;

      if (isLanguageSubject(subjectCode, subject.name)) {
        handleToggleLanguageSubject(`LANGUAGE|${subjectCode}`);
      } else if (isAdditionalSubject(subjectCode, subject.name)) {
        handleToggleOptionalSubject(`ADDITIONAL SUBJECT (OPTIONAL)|${subjectCode}`);
      } else if (isNSQFSubject(subjectCode, subject.name)) {
        handleToggleOptionalSubject(`NSQF SUBJECT (OPTIONAL)|${subjectCode}`);
      } else {
        // Try to find main group
        const mainGroupName = getMainGroupName(subjectCode, subject.name);
        if (mainGroupName) {
          handleToggleMainGroupSubject(`${mainGroupName}|${subjectCode}`);
        }
      }
    }
  }, [classSubjects, isLanguageSubject, isAdditionalSubject, isNSQFSubject, getMainGroupName, handleToggleLanguageSubject, handleToggleOptionalSubject, handleToggleMainGroupSubject, isFieldReadOnly]);

  // Function to get subject display name from stored value (handles both "CODE" and "GROUP|CODE" formats)
  const getSubjectDisplayName = useCallback((storedValue: string) => {
    const subjectCode = getBackendSubjectCode(storedValue);
    const subject = findSubjectByCode(subjectCode);
    if (subject) {
      return `(${subjectCode}) ${subject.name}`;
    }
    // Fallback: try to extract from stored value
    return storedValue.includes('|') ? storedValue.split('|')[1] : storedValue;
  }, [findSubjectByCode, getBackendSubjectCode]);

  // Function to get subject code for display
  const getSubjectCodeForDisplay = useCallback((fullSubjectName: string) => {
    if (!fullSubjectName) return '';
    const codeMatch = fullSubjectName.match(/\((\d+)\)/);
    return codeMatch ? codeMatch[1] : fullSubjectName;
  }, []);

  // Function to toggle group expansion
  const handleToggleGroup = useCallback((groupName: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  }, []);

  // Function to toggle subgroup expansion
  const handleToggleSubgroup = useCallback((groupName: string, subgroupName: string) => {
    const key = `${groupName}-${subgroupName}`;
    setExpandedSubgroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Handle subject selection
    if (name === 'addSubject') {
      // Skip if this is a manual selection (handled by MenuItem onClick)
      if (isManualSelectionRef.current) {
        isManualSelectionRef.current = false;
        setSubjectSelectValue('');
        return;
      }

      if (value) {
        handleAddSubject(value);
        // Reset the select value after adding subject but keep dropdown open
        setSubjectSelectValue('');
        // Keep dropdown open by setting it to open again
        setTimeout(() => {
          setSubjectDropdownOpen(true);
        }, 0);
      }
      return;
    }

    // Mobile number validation
    if (name === 'mobileNo') {
      // Only allow digits and limit to 10 digits
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: digitsOnly }));

      // Validate mobile number
      if (digitsOnly.length > 0) {
        // Check if first digit is valid (9, 8, 7, or 6)
        const firstDigit = digitsOnly[0];
        if (!['9', '8', '7', '6'].includes(firstDigit)) {
          setFieldErrors(prev => ({ ...prev, mobileNo: 'Mobile number must start with 9, 8, 7, or 6' }));
        } else if (digitsOnly.length !== 10) {
          setFieldErrors(prev => ({ ...prev, mobileNo: 'Mobile number must be exactly 10 digits' }));
        } else {
          setFieldErrors(prev => ({ ...prev, mobileNo: false }));
        }
      } else {
        // Clear error if field is empty (will be caught by required validation)
        setFieldErrors(prev => ({ ...prev, mobileNo: false }));
      }
      return;
    }

    // Account Number validation - only digits, 9-18 digits max
    if (name === 'accountNo') {
      // Only allow digits and limit to 18 digits
      const digitsOnly = value.replace(/\D/g, '').slice(0, 18);
      setFormData(prev => ({ ...prev, [name]: digitsOnly }));

      // Clear error when user types (validation will happen on submit)
      if (fieldErrors.accountNo) {
        setFieldErrors(prev => ({ ...prev, accountNo: false }));
      }
      return;
    }

    // IFSC Code validation - 11 characters, alphanumeric, format: ABCD0123456 (4 letters + 0 + 6 digits)
    if (name === 'ifcCode') {
      // Convert to uppercase and only allow alphanumeric, limit to 11 characters
      const upperValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
      setFormData(prev => ({ ...prev, [name]: upperValue }));

      // Clear error when user types (validation will happen on submit)
      if (fieldErrors.ifcCode) {
        setFieldErrors(prev => ({ ...prev, ifcCode: false }));
      }
      return;
    }

    // Gmail validation - email format
    if (name === 'gmail') {
      setFormData(prev => ({ ...prev, [name]: value }));

      // Clear error when user types (validation will happen on submit)
      if (fieldErrors.gmail) {
        setFieldErrors(prev => ({ ...prev, gmail: false }));
      }
      return;
    }

    // Clear error for this field when user starts typing
    if (fieldErrors[name as keyof typeof fieldErrors]) {
      setFieldErrors(prev => ({ ...prev, [name]: false }));
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  }, [handleAddSubject, fieldErrors]);

  // Handle date changes for DatePicker components
  const handleDateChange = useCallback((name: string, value: Date | null) => {
    try {
      // Clear error for this field when user selects a date
      if (fieldErrors[name as keyof typeof fieldErrors]) {
        setFieldErrors(prev => ({ ...prev, [name]: false }));
      }

      if (value && isValid(value)) {
        const formattedDate = format(value, 'dd/MM/yyyy');
        setFormData(prev => ({ ...prev, [name]: formattedDate }));
      } else {
        // Set empty string if no valid date is provided (no default)
        setFormData(prev => ({ ...prev, [name]: '' }));
      }
    } catch (error) {
      // If formatting fails, set empty string (no default)
      setFormData(prev => ({ ...prev, [name]: '' }));
    }
  }, [fieldErrors]);

  const handleMobileBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    if (isReadOnly || isFieldReadOnly('mobileNo')) return;

    const mobileNo = e.target.value.replace(/\D/g, '');
    if (mobileNo.length === 10) {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/application-forms/check-mobile`, {
          params: { mobileNo },
          headers: {
            'X-Tenant-ID': String(axios.defaults.headers.common['X-Tenant-ID'] || '')
          }
        });
        
        if (response.data && response.data.exists) {
          setSiblingStudentName(response.data.studentName || 'another student');
          setSiblingDialogOpen(true);
        } else {
          setFormData(prev => ({ ...prev, isSibling: false }));
        }
      } catch (error) {
        console.error("Error checking mobile existence", error);
      }
    }
  };

  const handleSiblingYes = () => {
    setFormData(prev => ({ ...prev, isSibling: true }));
    setSiblingDialogOpen(false);
  };

  const handleSiblingNo = () => {
    setFormData(prev => ({ ...prev, mobileNo: '', isSibling: false }));
    setFieldErrors(prev => ({ ...prev, mobileNo: 'Kindly enter a different mobile number.' }));
    setSiblingDialogOpen(false);
  };

  // Helper function to ensure dates are always valid in dd/MM/yyyy format (as expected by backend)
  const ensureValidDates = useCallback((data: ApplicationFormData) => {
    // Ensure dateOfBirth is valid
    let dateOfBirth = data.dateOfBirth;
    if (dateOfBirth) {
      try {
        const parsedDate = new Date(dateOfBirth);
        if (isNaN(parsedDate.getTime())) {
          dateOfBirth = ''; // Set to empty if invalid
        } else {
          dateOfBirth = format(parsedDate, 'dd/MM/yyyy');
        }
      } catch (error) {
        dateOfBirth = ''; // Set to empty if parsing fails
      }
    }

    // Ensure admissionDate is valid
    let admissionDate = data.admissionDate;
    if (admissionDate) {
      try {
        const parsedDate = new Date(admissionDate);
        if (isNaN(parsedDate.getTime())) {
          admissionDate = ''; // Set to empty if invalid
        } else {
          admissionDate = format(parsedDate, 'dd/MM/yyyy');
        }
      } catch (error) {
        admissionDate = ''; // Set to empty if parsing fails
      }
    }

    return {
      ...data,
      dateOfBirth,
      admissionDate,
    };
  }, []);

  const SectionTitle = ({ title }: { title: string }) => (
    <Box sx={{
      p: 1,
      mb: 2,
      backgroundColor: '#f8f9fb',
      borderLeft: '4px solid #0168EA',
      borderRadius: 1,
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }}>
      <Typography variant="h6" sx={{
        color: '#0168EA',
        fontWeight: 600,
        mb: 0
      }}>
        {t(title)}
      </Typography>
    </Box>
  );

  const handleSubmit = (event?: any) => {
    if (event?.preventDefault) event.preventDefault();
    if (event?.stopPropagation) event.stopPropagation();
    if (isReadOnly) return;

    // Check all required fields and set errors
    const errors: typeof fieldErrors = {};
    const missingFields = [];

    if (!formData.studentName || formData.studentName.trim() === '') {
      missingFields.push('studentName');
      errors.studentName = true;
    }
    if (!formData.fatherName || formData.fatherName.trim() === '') {
      missingFields.push('fatherName');
      errors.fatherName = true;
    }
    if (!formData.motherName || formData.motherName.trim() === '') {
      missingFields.push('motherName');
      errors.motherName = true;
    }
    if (!formData.dateOfBirth || formData.dateOfBirth.trim() === '') {
      missingFields.push('dateOfBirth');
      errors.dateOfBirth = true;
    }
    if (!formData.gender || formData.gender.trim() === '') {
      missingFields.push('gender');
      errors.gender = true;
    }
    if (!formData.mobileNo || formData.mobileNo.trim() === '') {
      missingFields.push('mobileNo');
      errors.mobileNo = true;
    } else {
      const mobileDigits = formData.mobileNo.replace(/\D/g, '');
      const firstDigit = mobileDigits[0];
      if (!['9', '8', '7', '6'].includes(firstDigit)) {
        missingFields.push('mobileNo');
        errors.mobileNo = 'Mobile number must start with 6, 7, 8, or 9';
      } else if (mobileDigits.length !== 10) {
        missingFields.push('mobileNo');
        errors.mobileNo = 'Mobile number must be exactly 10 digits';
      }
    }
    if (!formData.medium || formData.medium.trim() === '') {
      missingFields.push('medium');
      errors.medium = true;
    }

    // Validate Gmail format (only if value is provided)
    if (formData.gmail && formData.gmail.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.gmail.trim())) {
        errors.gmail = 'Please enter a valid email address';
        missingFields.push('gmail');
      }
    }

    // Validate IFSC Code format (only if value is provided)
    if (formData.ifcCode && formData.ifcCode.trim() !== '') {
      const ifscCode = formData.ifcCode.trim().toUpperCase();
      // IFSC format: 4 letters + 0 + 6 digits (11 characters total)
      const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
      if (ifscCode.length !== 11 || !ifscRegex.test(ifscCode)) {
        errors.ifcCode = 'IFSC code must be 11 characters (e.g., ABCD0123456)';
        missingFields.push('ifcCode');
      }
    }

    // Validate Account Number format (only if value is provided)
    if (formData.accountNo && formData.accountNo.trim() !== '') {
      const accountDigits = formData.accountNo.replace(/\D/g, '');
      if (accountDigits.length < 9 || accountDigits.length > 18) {
        errors.accountNo = 'Account number must be between 9 and 18 digits';
        missingFields.push('accountNo');
      }
    }

    // Set field errors to show helper text
    setFieldErrors(errors);

    // Skip subject validation if subjects array is empty (user wants to remove all subjects)
    if (formData.subjects.length === 0) {
      // Clear stream field when all subjects are removed
      const finalFormData = {
        ...formData,
        subjects: [], // Send empty array
        stream: '', // Send empty string for stream
        admissionClass: selectedClass?.name || formData.admissionClass,
        section: canSchoolAdminEditSection ? formData.section : (selectedClass?.section || formData.section),
      };
      onSubmit(finalFormData);
      return;
    }

    // Validate HUMANITIES GROUP subject selection rules
    if (selectedMainGroup === "HUMANITIES GROUP" && isEleventhTwelfthClass && formData.subjects.length > 0) {
      const humanitiesSubjects = formData.subjects.filter(storedValue =>
        storedValue.startsWith("HUMANITIES GROUP|")
      );

      const subgroupACodes = ["110", "120", "130", "140"];
      const subgroupBCodes = ["150", "151", "162", "163", "164", "165", "166", "167", "168"];

      const subgroupASubjects = humanitiesSubjects.filter(storedValue => {
        const code = extractSubjectCode(storedValue);
        return subgroupACodes.includes(code);
      });
      const subgroupBSubjects = humanitiesSubjects.filter(storedValue => {
        const code = extractSubjectCode(storedValue);
        return subgroupBCodes.includes(code);
      });

      const subgroupACount = subgroupASubjects.length;
      const subgroupBCount = subgroupBSubjects.length;

      // Validation: Must have at least 2 from Subgroup A
      if (subgroupACount < 2) {
        showError(t('humanities_subgroup_a_minimum') || 'Please select at least 2 subjects from Subgroup A in HUMANITIES GROUP.');
        return;
      }

      // Validation: If 2 from A, must have exactly 1 from B
      if (subgroupACount === 2 && subgroupBCount !== 1) {
        showError(t('humanities_subgroup_b_required') || 'If you select 2 subjects from Subgroup A, you must select exactly 1 subject from Subgroup B.');
        return;
      }

      // Validation: If 3 from A, must have 0 from B
      if (subgroupACount === 3 && subgroupBCount !== 0) {
        showError(t('humanities_subgroup_b_disabled') || 'If you select 3 subjects from Subgroup A, Subgroup B should be empty.');
        return;
      }
    }

    // Validate COMMERCE GROUP subject selection rules
    if (selectedMainGroup === "COMMERCE GROUP" && isEleventhTwelfthClass && formData.subjects.length > 0) {
      const commerceSubjects = formData.subjects.filter(storedValue =>
        storedValue.startsWith("COMMERCE GROUP|")
      );

      // Only validate if there are COMMERCE GROUP subjects
      if (commerceSubjects.length > 0) {
        const subgroupACodes = ["310", "320"]; // Business Studies, Book Keeping & Accountancy
        const subgroupBCodes = ["150", "151", "140"]; // Mathematics, Informatic Practices, Economics

        const subgroupASubjects = commerceSubjects.filter(storedValue => {
          const code = extractSubjectCode(storedValue);
          return subgroupACodes.includes(code);
        });
        const subgroupBSubjects = commerceSubjects.filter(storedValue => {
          const code = extractSubjectCode(storedValue);
          return subgroupBCodes.includes(code);
        });

        const subgroupACount = subgroupASubjects.length;
        const subgroupBCount = subgroupBSubjects.length;

        // Validation: Must have exactly 2 from Subgroup A (both required)
        if (subgroupACount !== 2) {
          showError(t('commerce_subgroup_a_required') || 'Please select both subjects from Subgroup A in COMMERCE GROUP (Business Studies and Book Keeping & Accountancy).');
          return;
        }

        // Validation: Must have exactly 1 from Subgroup B
        if (subgroupBCount !== 1) {
          showError(t('commerce_subgroup_b_required') || 'Please select exactly 1 subject from Subgroup B in COMMERCE GROUP.');
          return;
        }
      }
    }

    // Validate LANGUAGE GROUP subject selection rules
    // Only validate if there are actually LANGUAGE GROUP subjects selected
    const hasLanguageGroupSubjects = formData.subjects.some(storedValue =>
      storedValue.startsWith("LANGUAGE GROUP|")
    );

    if (selectedMainGroup === "LANGUAGE GROUP" && isEleventhTwelfthClass && hasLanguageGroupSubjects) {
      const subgroupACodes = ["051", "052", "053", "054", "055"]; // Hindi, English, Sanskrit, Marathi, Urdu
      const subgroupBCodes = ["110", "120", "130", "140", "166"]; // History, Geography, Political Science, Economics, Sociology

      // Count subjects that are actually in LANGUAGE GROUP (with "LANGUAGE GROUP|" prefix)
      // This excludes subjects from the initial LANGUAGE section (with "LANGUAGE|" prefix)
      const subgroupASubjects: string[] = [];
      const subgroupBSubjects: string[] = [];

      formData.subjects.forEach(storedValue => {
        // Only count subjects that are in LANGUAGE GROUP, not in initial LANGUAGE section
        if (storedValue.startsWith("LANGUAGE GROUP|")) {
          const code = extractSubjectCode(storedValue);
          if (subgroupACodes.includes(code)) {
            subgroupASubjects.push(storedValue);
          } else if (subgroupBCodes.includes(code)) {
            subgroupBSubjects.push(storedValue);
          }
        }
      });

      const subgroupACount = subgroupASubjects.length;
      const subgroupBCount = subgroupBSubjects.length;

      // Validation: Must have exactly 1 from Subgroup A
      if (subgroupACount !== 1) {
        showError(t('language_group_subgroup_a_required') || 'Please select exactly 1 language from Subgroup A in LANGUAGE GROUP.');
        return;
      }

      // Validation: Must have exactly 2 from Subgroup B
      if (subgroupBCount !== 2) {
        showError(t('language_group_subgroup_b_required') || 'Please select exactly 2 subjects from Subgroup B in LANGUAGE GROUP.');
        return;
      }
    }

    // Prevent form submission if any required fields are missing or validation fails
    if (missingFields.length > 0) {
      return;
    }

    // Clear errors if validation passes
    setFieldErrors({});

    // Save draft data before submitting (only for create mode, not edit)
    if (!isEdit && !isStudentEdit) {
      saveApplicationFormDraft(formData);
    }

    // Ensure class data is always from selected class
    // Convert subjects from "GROUP|CODE" format back to just codes for backend
    // Order subjects: First 2 languages, then 3 main group subjects, then 1 optional (if any)
    let subjectsForBackend: string[] = [];

    if (isEleventhTwelfthClass) {
      // Separate subjects by category
      const languageSubjects: string[] = [];
      const mainGroupSubjects: string[] = [];
      const optionalSubjects: string[] = [];

      formData.subjects.forEach((storedValue: string) => {
        const subjectCode = extractSubjectCode(storedValue);

        // Check if it's a language subject (from LANGUAGE section, not LANGUAGE GROUP)
        if (storedValue.startsWith('LANGUAGE|') && !storedValue.startsWith('LANGUAGE GROUP|')) {
          languageSubjects.push(subjectCode);
        }
        // Check if it's an optional subject
        else if (storedValue.startsWith('ADDITIONAL SUBJECT (OPTIONAL)|') ||
          storedValue.startsWith('NSQF SUBJECT (OPTIONAL)|')) {
          optionalSubjects.push(subjectCode);
        }
        // Otherwise, it's a main group subject (includes LANGUAGE GROUP subjects)
        else {
          mainGroupSubjects.push(subjectCode);
        }
      });

      // Order: First 2 languages, then 3 main group subjects, then 1 optional
      // This ensures consistent ordering for editing: [lang1, lang2, main1, main2, main3, optional?]
      subjectsForBackend = [
        ...languageSubjects.slice(0, 2), // First 2 languages (always from LANGUAGE section)
        ...mainGroupSubjects.slice(0, 3), // Next 3 main group subjects (includes LANGUAGE GROUP if selected)
        ...optionalSubjects.slice(0, 1)   // Last 1 optional subject (if any)
      ];
    } else {
      // For other classes, just extract codes in order
      subjectsForBackend = formData.subjects.map((storedValue: string) => {
        return getBackendSubjectCode(storedValue);
      });
    }

    // Ensure stream field contains the group name (not the code)
    // If subjects array is empty, clear stream field
    let streamValue = '';
    if (subjectsForBackend.length > 0) {
      streamValue = formData.stream || '';
      if (selectedStream && !streamValue) {
        // If stream is selected but formData.stream is not set, get the group name
        const streamOption = streamOptions.find(opt => opt.value === selectedStream);
        if (streamOption) {
          streamValue = streamOption.groupName;
        }
      } else if (selectedStream && streamValue && streamOptions.find(opt => opt.value === streamValue)) {
        // If formData.stream contains a code (old format), convert to group name
        const streamOption = streamOptions.find(opt => opt.value === streamValue);
        if (streamOption) {
          streamValue = streamOption.groupName;
        }
      }
    }

    const finalFormData = {
      ...formData,
      subjects: subjectsForBackend, // Send just codes to backend (empty array if no subjects)
      stream: streamValue, // Empty string if no subjects, otherwise group name
      admissionClass: selectedClass?.name || formData.admissionClass,
      section: canSchoolAdminEditSection ? formData.section : (selectedClass?.section || formData.section),
    };

    // Submit the form data
    onSubmit(finalFormData);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={() => { }} // Prevent closing on backdrop click
        maxWidth="lg"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit,
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#f8f9fb',
          borderBottom: '2px solid #e0e7ff',
          color: '#0168EA',
          fontWeight: 600,
          fontSize: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>
            {isStudentProfileUpdate
              ? t('profile_details')
              : isStudentEdit
                ? (isEdit ? t('edit_student') : t('add_new_student'))
                : (isEdit ? t('edit_application_form') : t('add_application_form'))
            }
          </span>
          <Button
            onClick={() => {
              // Save draft data when closing modal (only for create mode and if form has data)
              if (!isEdit && !isStudentEdit) {
                // Check if form has any meaningful data (not just empty/default values)
                const hasData = formData.studentName ||
                  formData.fatherName ||
                  formData.motherName ||
                  formData.dateOfBirth ||
                  formData.gender ||
                  formData.mobileNo ||
                  formData.medium ||
                  formData.applicationNo ||
                  formData.rollNo ||
                  formData.enrolmentNo ||
                  (formData.subjects && formData.subjects.length > 0);

                if (hasData) {
                  saveApplicationFormDraft(formData);
                }
              }
              onClose();
            }}
            sx={{
              minWidth: 'auto',
              p: 1,
              color: '#0168EA',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'transparent',
              },
              '&:focus': {
                backgroundColor: 'transparent',
              }
            }}
          >
            ✕
          </Button>
        </DialogTitle>
        <DialogContent>
          <Paper sx={{ p: 2 }}>
            {/* School Header Information - Only show for application forms, not for student editing */}
            {!isStudentEdit && (
              <>
                <Box sx={{
                  mb: 1,
                  p: 1.5,
                  background: '#3092e3',
                  borderRadius: 2,
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(1, 104, 234, 0.3)'
                }}>
                  <Typography variant="h4" fontWeight="bold" sx={{ mb: 2, textAlign: 'center', color: 'white' }}>
                    {t("application_form_a")}
                  </Typography>

                  <Grid container spacing={2} sx={{ ml: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                        <strong>{t("name_of_school")}:</strong> {schoolData?.nameofSchool || t("school_name_not_available")}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                        <strong>{t("school_code")}:</strong> {schoolData?.schoolCode || t("code_not_available")}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                        <strong>{t("district")}:</strong> {schoolData?.district || t("district_not_available")}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                        <strong>{t("dise_code")}:</strong> {schoolData?.diseCode || t("dise_code_not_available")}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 4, borderColor: '#e0e7ff', borderWidth: 1 }} />
              </>
            )}

            {/* Section 1: Application & Enrollment Details */}
            <SectionTitle title="application_enrollment_details" />
            <Grid container spacing={1.7}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('application_no')}
                  name="applicationNo"
                  value={formData.applicationNo}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('applicationNo'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('roll_no')}
                  name="rollNo"
                  value={formData.rollNo}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('rollNo'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('enrolment_no')}
                  name="enrolmentNo"
                  value={formData.enrolmentNo}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('enrolmentNo'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('scholar_no')}
                  name="scholarNo"
                  value={formData.scholarNo}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('scholarNo'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('sssmid')}
                  name="sssmid"
                  value={formData.sssmid}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('sssmid'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('pen_no')}
                  name="penNo"
                  value={formData.penNo}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('penNo'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t("appar_id_no")}
                  name="apparId"
                  value={formData.apparId}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('apparId'),
                  }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4, borderColor: '#e0e7ff', borderWidth: 1 }} />

            {/* Section 2: Student Personal Information */}
            <SectionTitle title="personal_information" />
            <Grid container spacing={1.7}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('student_name')}
                  name="studentName"
                  value={formData.studentName}
                  onChange={handleChange}
                  required
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': { color: 'red' } }}
                  helperText={fieldErrors.studentName ? <span style={{ color: 'red' }}>Required field</span> : ''}
                  error={fieldErrors.studentName}
                  InputProps={{
                    readOnly: isFieldReadOnly('studentName'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('father_name')}
                  name="fatherName"
                  value={formData.fatherName}
                  onChange={handleChange}
                  required
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': { color: 'red' } }}
                  helperText={fieldErrors.fatherName ? <span style={{ color: 'red' }}>Required field</span> : ''}
                  error={fieldErrors.fatherName}
                  InputProps={{
                    readOnly: isFieldReadOnly('fatherName'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('mother_name')}
                  name="motherName"
                  value={formData.motherName}
                  onChange={handleChange}
                  required
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': { color: 'red' } }}
                  helperText={fieldErrors.motherName ? <span style={{ color: 'red' }}>Required field</span> : ''}
                  error={fieldErrors.motherName}
                  InputProps={{
                    readOnly: isFieldReadOnly('motherName'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label={t('caste_certificate')}
                  name="casteCertificate"
                  value={formData.casteCertificate}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  disabled={isFieldReadOnly('casteCertificate')}
                >
                  <MenuItem value="Yes">{t('yes')}</MenuItem>
                  <MenuItem value="No">{t('no')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label={t('gender')}
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  required
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': { color: 'red' } }}
                  helperText={fieldErrors.gender ? <span style={{ color: 'red' }}>Required field</span> : ''}
                  error={fieldErrors.gender}
                  disabled={isFieldReadOnly('gender')}
                >
                  <MenuItem value="M">{t('male')}</MenuItem>
                  <MenuItem value="F">{t('female')}</MenuItem>
                  <MenuItem value="O">{t('other')}</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('religion')}
                  name="religion"
                  value={formData.religion}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('religion'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label={t('caste')}
                  name="caste"
                  value={formData.caste}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  disabled={isFieldReadOnly('caste')}
                >
                  {CasteOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('sub_caste')}
                  name="subCaste"
                  value={formData.subCaste}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('subCaste'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('aadhar_no')}
                  name="aadharNo"
                  value={formData.aadharNo}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('aadharNo'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label={t('date_of_birth')}
                  value={safeParseDateString(formData.dateOfBirth)}
                  onChange={(value) => handleDateChange('dateOfBirth', value)}
                  disabled={isFieldReadOnly('dateOfBirth')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      size: 'small',
                      margin: 'dense',
                      sx: { backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': { color: 'red' } },
                      helperText: fieldErrors.dateOfBirth ? <span style={{ color: 'red' }}>Required field</span> : '',
                      error: fieldErrors.dateOfBirth
                    }
                  }}
                  format="dd/MM/yyyy"
                />
              </Grid>
            </Grid>


            <Divider sx={{ my: 4, borderColor: '#e0e7ff', borderWidth: 1 }} />

            {/* Section 3: Contact & Address Information */}
            <SectionTitle title="contact_address" />
            <Grid container spacing={1.7}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t("house_no_building_apartment")}
                  name="houseNo"
                  value={formData.houseNo}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('houseNo'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t("street_locality_sector")}
                  name="street"
                  value={formData.street}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('street'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('landmark')}
                  name="landmark"
                  value={formData.landmark}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('landmark'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t("area_locality_sector")}
                  name="area"
                  value={formData.area}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('area'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t("village_town_city")}
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('city'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('post_office')}
                  name="postOffice"
                  value={formData.postOffice}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('postOffice'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('district')}
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('district'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('state')}
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('state'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('pin_code')}
                  name="pinCode"
                  value={formData.pinCode}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('pinCode'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('mobile_no')}
                  name="mobileNo"
                  value={formData.mobileNo}
                  onChange={handleChange}
                  onBlur={handleMobileBlur}
                  required
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': { color: 'red' } }}
                  helperText={fieldErrors.mobileNo ? <span style={{ color: 'red' }}>{typeof fieldErrors.mobileNo === 'string' ? fieldErrors.mobileNo : 'Required field'}</span> : ''}
                  error={!!fieldErrors.mobileNo}
                  InputProps={{
                    readOnly: isFieldReadOnly('mobileNo'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('gmail')}
                  name="gmail"
                  type="email"
                  value={formData.gmail}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  helperText={fieldErrors.gmail ? <span style={{ color: 'red' }}>{typeof fieldErrors.gmail === 'string' ? fieldErrors.gmail : 'Invalid email format'}</span> : ''}
                  error={!!fieldErrors.gmail}
                  InputProps={{
                    readOnly: isFieldReadOnly('gmail'),
                  }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4, borderColor: '#e0e7ff', borderWidth: 1 }} />

            {/* Section 4: Bank & Financial Information */}
            <SectionTitle title="bank_financial" />
            <Grid container spacing={1.7}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('bank_name')}
                  name="bankName"
                  value={formData.bankName}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('bankName'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('ifsc_code')}
                  name="ifcCode"
                  value={formData.ifcCode}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  helperText={fieldErrors.ifcCode ? <span style={{ color: 'red' }}>{typeof fieldErrors.ifcCode === 'string' ? fieldErrors.ifcCode : 'Invalid IFSC code format'}</span> : ''}
                  error={!!fieldErrors.ifcCode}
                  InputProps={{
                    readOnly: isFieldReadOnly('ifcCode'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('account_no')}
                  name="accountNo"
                  value={formData.accountNo}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  helperText={fieldErrors.accountNo ? <span style={{ color: 'red' }}>{typeof fieldErrors.accountNo === 'string' ? fieldErrors.accountNo : 'Account number must be between 9 and 18 digits'}</span> : ''}
                  error={!!fieldErrors.accountNo}
                  InputProps={{
                    readOnly: isFieldReadOnly('accountNo'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('annual_income')}
                  name="annualIncome"
                  value={formData.annualIncome}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('annualIncome'),
                  }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4, borderColor: '#e0e7ff', borderWidth: 1 }} />

            {/* Section 5: Admission Details */}
            <SectionTitle title="admission_details" />
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label={t('admission_date')}
                  value={safeParseDateString(formData.admissionDate)}
                  onChange={(value) => handleDateChange('admissionDate', value)}
                  disabled={isFieldReadOnly('admissionDate')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      margin: 'dense',
                      sx: { backgroundColor: '#f8f9fb' }
                    }
                  }}
                  format="dd/MM/yyyy"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('admission_class')}
                  name="admissionClass"
                  value={selectedClass?.name || formData.admissionClass}
                  required
                  size="small"
                  margin="dense"
                  sx={{
                    backgroundColor: '#f8f9fb',
                    '& .MuiInputBase-input': {
                      color: '#666',
                      fontWeight: 500
                    },
                    '& .MuiFormLabel-asterisk': { color: 'red' }
                  }}
                  InputProps={{
                    readOnly: true,
                  }}
                // helperText={t("auto_filled_from_selected_class")}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label={t('section')}
                  name="section"
                  value={canSchoolAdminEditSection ? formData.section : (selectedClass?.section || formData.section)}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{
                    backgroundColor: '#f8f9fb',
                    '& .MuiInputBase-input': {
                      color: '#666',
                      fontWeight: 500
                    }
                  }}
                  disabled={!canSchoolAdminEditSection}
                // helperText={t("auto_filled_from_selected_class")}
                >
                  {sectionOptions.map((section) => (
                    <MenuItem key={section} value={section}>
                      {section}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label={t('medium')}
                  name="medium"
                  value={formData.medium}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': { color: 'red' } }}
                  required
                  helperText={fieldErrors.medium ? <span style={{ color: 'red' }}>Required field</span> : ''}
                  error={fieldErrors.medium}
                  disabled={isFieldReadOnly('medium')}
                >
                  {getAvailableMediumOptions().map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label={t('Admission Type')}
                  name="admissionType"
                  value={formData.admissionType || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{
                    backgroundColor: '#f8f9fb',
                    ...(isEdit && formData.admissionType && {
                      '& .MuiInputBase-input': {
                        fontWeight: 500
                      }
                    })
                  }}
                  disabled={isFieldReadOnly('admissionType')}
                >
                  <MenuItem value="REGULAR">{t('REGULAR')}</MenuItem>
                  <MenuItem value="PRIVATE">{t('PRIVATE')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('cwsn_code')}
                  name="cwsnCode"
                  value={formData.cwsnCode}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('cwsnCode'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('sambal_kramank')}
                  name="sambalKramank"
                  value={formData.sambalKramank}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  InputProps={{
                    readOnly: isFieldReadOnly('sambalKramank'),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  label="House"
                  name="houseId"
                  value={formData.houseId || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  disabled={isFieldReadOnly('house')}
                >
                  <MenuItem value="">
                    <em>Select House</em>
                  </MenuItem>
                  {(getAllHouseQuery.data || []).map((h) => (
                    <MenuItem key={h.id} value={h.id || ''}>
                      {h.houseName || h.houseCode}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label="RTE"
                  name="rte"
                  value={formData.rte || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  disabled={isFieldReadOnly('rte')}
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            <Divider sx={{ my: 4, borderColor: '#e0e7ff', borderWidth: 1 }} />

            {/* Section 6: Subjects and Documents */}
            <SectionTitle title="subjects_and_documents" />
            <Grid container spacing={2}>
              {/* For Class 11th/12th: New Subject Selection UI with Rules */}
              {isEleventhTwelfthClass ? (
                <Grid item xs={12}>
                  {subjectsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : !classSubjects || classSubjects.length === 0 ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      {t("no_subjects_available_for_class")}
                    </Alert>
                  ) : (() => {
                    const grouped = getGroupedSubjectOptions();
                    const selectedLanguages = getSelectedLanguageSubjects();
                    const selectedMainGroupSubjects = getSelectedMainGroupSubjects();
                    const allMainGroupSubjects = getAllMainGroupSubjects();
                    const selectedOptional = getSelectedOptionalSubject();
                    // Show optional groups if ANY main group has 3 subjects selected
                    const showOptionalGroups = allMainGroupSubjects.length >= 3;

                    // Helper function to render subjects
                    const renderSubjects = (subjects: any[], maxSelection: number, currentCount: number, onToggle: (value: string) => void, checkTotalLimit: boolean = false, groupName?: string, isSubgroupDisabled: boolean = false): React.ReactElement => {
                      const totalSubjectsCount = formData.subjects.length;
                      const isTotalLimitReached = checkTotalLimit && totalSubjectsCount >= 6;
                      return (
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                          {subjects.map((subject: any) => {
                            // Extract subject code from subject.value (format: "GROUP|CODE" or just "CODE")
                            const subjectCode = extractSubjectCode(subject.value);
                            // Extract the expected group prefix from subject.value
                            const expectedGroupPrefix = subject.value.includes('|') ? subject.value.split('|')[0] : null;

                            // Check if this subject is selected - must match BOTH code AND group prefix
                            // This ensures subjects with same name but different codes are distinguished correctly
                            const isSelected = formData.subjects.some((storedValue: string) => {
                              const storedCode = extractSubjectCode(storedValue);
                              // For optional groups, must match both code AND group prefix
                              if (groupName === 'ADDITIONAL SUBJECT (OPTIONAL)' || groupName === 'NSQF SUBJECT (OPTIONAL)') {
                                return storedCode === subjectCode &&
                                  storedValue.startsWith(expectedGroupPrefix || '');
                              }
                              // For other groups, check by code (but prefer exact match if group prefix is available)
                              if (expectedGroupPrefix && storedValue.startsWith(expectedGroupPrefix)) {
                                return storedCode === subjectCode;
                              }
                              // Fallback: check by code only (for backward compatibility)
                              return storedCode === subjectCode;
                            });
                            // If the entire subgroup is disabled, disable all subjects in it
                            // For optional groups (Additional/NSQF), allow clicking another subject even when maxSelection is reached
                            // But for main groups, strictly enforce the limit
                            const isOptionalGroup = groupName === 'ADDITIONAL SUBJECT (OPTIONAL)' || groupName === 'NSQF SUBJECT (OPTIONAL)';
                            // For optional groups, allow switching even at max (to replace selection)
                            // For main groups, disable when limit is reached
                            const hasReachedGroupLimit = !isOptionalGroup && currentCount >= maxSelection;
                            const isDisabled = isFieldReadOnly('subjects') || isSubgroupDisabled || (!isSelected && (hasReachedGroupLimit || isTotalLimitReached));
                            return (
                              <Grid item xs={12} sm={6} md={4} key={subject.value}>
                                <Box
                                  onClick={() => {
                                    if (!isDisabled) {
                                      onToggle(subject.value);
                                    }
                                  }}
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: isSelected ? '#4caf50' : '#e0e0e0',
                                    backgroundColor: isSelected ? '#e8f5e9' : 'transparent',
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    transition: 'all 0.2s ease-in-out',
                                    opacity: isDisabled ? 0.5 : 1,
                                    '&:hover': {
                                      backgroundColor: isDisabled ? 'transparent' : (isSelected ? '#c8e6c9' : '#f5f5f5'),
                                      borderColor: isDisabled ? '#e0e0e0' : (isSelected ? '#4caf50' : '#bdbdbd'),
                                    },
                                  }}
                                >
                                  <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                                    {subject.label}
                                  </Typography>
                                  {isSelected && (
                                    <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                                  )}
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      );
                    };

                    return (
                      <Box sx={{ mt: 2 }}>
                        {/* Step 1: Language Group - Always visible, expanded by default */}
                        {grouped.languageGroup && (
                          <Paper
                            elevation={1}
                            sx={{
                              mb: 2,
                              border: '1px solid #e0e7ff',
                              borderRadius: 2,
                              overflow: 'hidden'
                            }}
                          >
                            <Box
                              onClick={() => setLanguageGroupExpanded(!languageGroupExpanded)}
                              sx={{
                                p: 2,
                                cursor: 'pointer',
                                backgroundColor: languageGroupExpanded ? '#e3f2fd' : '#f8f9fb',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                transition: 'all 0.3s ease-in-out',
                                '&:hover': {
                                  backgroundColor: '#e3f2fd',
                                }
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#0168EA', mb: 0.5 }}>
                                  {grouped.languageGroup.groupName}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.875rem' }}>
                                  {t('you_can_select_only_2_languages') || 'You can select only 2 languages.'}
                                </Typography>
                                {selectedLanguages.length > 0 && (
                                  <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 500, display: 'block', mt: 0.5 }}>
                                    {t('selected')}: {selectedLanguages.length}/2
                                  </Typography>
                                )}
                              </Box>
                              <IconButton size="small">
                                {languageGroupExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </Box>

                            <Collapse in={languageGroupExpanded}>
                              <Box sx={{ p: 2, backgroundColor: '#ffffff' }}>
                                {(() => {
                                  // Get languages selected in LANGUAGE GROUP Subgroup A
                                  const selectedInLanguageGroupSubgroupA = formData.subjects
                                    .filter(storedValue => storedValue.startsWith('LANGUAGE GROUP|'))
                                    .map(storedValue => {
                                      const code = extractSubjectCode(storedValue);
                                      return String(code).trim();
                                    });

                                  // Filter out languages that are already selected in LANGUAGE GROUP Subgroup A
                                  // But always include languages that are already selected in the initial LANGUAGE section
                                  const selectedLanguageCodes = selectedLanguages.map(storedValue => {
                                    const code = extractSubjectCode(storedValue);
                                    return String(code).trim();
                                  });

                                  const filteredLanguageSubjects = (grouped.languageGroup.subjects || []).filter((subject: any) => {
                                    const subjectCode = String(subject.subjectCode || subject.originalCode || '').trim();
                                    const isSelectedInLanguageStep = selectedLanguageCodes.includes(subjectCode);
                                    const isSelectedInLanguageGroup = selectedInLanguageGroupSubgroupA.includes(subjectCode);
                                    // Show if: already selected in language step OR not selected in language group
                                    return isSelectedInLanguageStep || !isSelectedInLanguageGroup;
                                  });

                                  return renderSubjects(
                                    filteredLanguageSubjects,
                                    2,
                                    selectedLanguages.length,
                                    handleToggleLanguageSubject,
                                    true // Check total limit
                                  );
                                })()}
                              </Box>
                            </Collapse>
                          </Paper>
                        )}

                        {/* Stream Selection - After Language Group */}
                        <Paper
                          elevation={1}
                          sx={{
                            mb: 2,
                            mt: 3,
                            border: '1px solid #e0e7ff',
                            borderRadius: 2,
                            overflow: 'hidden'
                          }}
                        >
                          <Box sx={{ p: 2, backgroundColor: '#f8f9fb' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0168EA', mb: 1 }}>
                              {t('select_stream') || 'Select Stream'}
                            </Typography>
                            {(() => {
                              const selectedLanguages = getSelectedLanguageSubjects();
                              const isStreamDisabled = selectedLanguages.length !== 2 || isFieldReadOnly('subjects');

                              // Get grouped subject options to filter streams based on available groups
                              const grouped = getGroupedSubjectOptions();

                              // Filter streamOptions to only show groups that have subjects created
                              const availableStreamOptions = streamOptions.filter(option => {
                                return grouped.mainGroups && Object.keys(grouped.mainGroups).includes(option.groupName);
                              });

                              return (
                                <TextField
                                  fullWidth
                                  select
                                  label={t('stream') || 'Stream'}
                                  name="stream"
                                  value={selectedStream || (() => {
                                    // If formData.stream is a group name, find the corresponding code
                                    const streamOption = availableStreamOptions.find(opt => opt.groupName === formData.stream);
                                    return streamOption ? streamOption.value : '';
                                  })() || ''}
                                  onChange={(e) => {
                                    const streamValue = e.target.value;
                                    setSelectedStream(streamValue);

                                    // Filter to keep ONLY language subjects when stream changes
                                    const preservedSubjects = formData.subjects.filter(storedValue => {
                                      // Extract code from stored value (format: "GROUP|CODE" or "CODE")
                                      const code = getBackendSubjectCode(storedValue);
                                      // Find subject to get name (prefer exact match)
                                      const subject = findSubjectByCode(code);
                                      const name = subject ? subject.name : '';

                                      // Check if it's a language
                                      // Use isLanguageSubject helper or check if it starts with LANGUAGE|
                                      return isLanguageSubject(code, name) ||
                                        storedValue.startsWith('LANGUAGE|') ||
                                        storedValue.startsWith('LANGUAGE GROUP|');
                                    });

                                    // Find the corresponding group name
                                    const streamOption = availableStreamOptions.find(opt => opt.value === streamValue);
                                    if (streamOption) {
                                      // Store the group name in formData.stream instead of the code
                                      setFormData(prev => ({
                                        ...prev,
                                        stream: streamOption.groupName,
                                        subjects: preservedSubjects // Keep only languages
                                      }));
                                      // Auto-select the corresponding main group
                                      handleToggleMainGroup(streamOption.groupName);
                                    } else {
                                      // If no stream selected, clear the stream field but keep languages
                                      setFormData(prev => ({
                                        ...prev,
                                        stream: '',
                                        subjects: preservedSubjects
                                      }));
                                    }
                                  }}
                                  size="small"
                                  margin="dense"
                                  sx={{ backgroundColor: '#ffffff' }}
                                  disabled={isStreamDisabled}
                                  helperText={selectedLanguages.length !== 2 ? (t('please_select_two_languages_first') || 'Please select 2 languages first') : ''}
                                >
                                  <MenuItem value="">
                                    <em>{t('select_stream') || 'Select a stream'}</em>
                                  </MenuItem>
                                  {availableStreamOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                      {option.label} (Group Code: {option.value})
                                    </MenuItem>
                                  ))}
                                </TextField>
                              );
                            })()}
                          </Box>
                        </Paper>

                        {/* Step 2: Main Groups - Show only selected stream's group */}
                        {selectedStream && (
                          <>
                            <Typography variant="subtitle2" sx={{ mb: 2, color: '#0168EA', fontWeight: 600, mt: 3 }}>
                              {t('selected_stream_group') || 'Selected Stream Group'}
                            </Typography>
                            {(() => {
                              // Find the group name for the selected stream
                              const streamOption = streamOptions.find(opt => opt.value === selectedStream);
                              if (!streamOption) return null;

                              const groupName = streamOption.groupName;
                              const groupData = (grouped.mainGroups as { [key: string]: any })?.[groupName];

                              if (!groupData) return null;

                              const isGroupSelected = selectedMainGroup === groupName;
                              const isGroupExpanded = expandedGroups.has(groupName);

                              return (
                                <Paper
                                  key={groupName}
                                  elevation={1}
                                  sx={{
                                    mb: 2,
                                    border: '1px solid',
                                    borderColor: isGroupSelected ? '#4caf50' : '#e0e7ff',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                  }}
                                >
                                  {/* Group Header with Checkbox */}
                                  <Box
                                    sx={{
                                      p: 2,
                                      display: 'flex',
                                      alignItems: 'center',
                                      backgroundColor: isGroupSelected ? '#e8f5e9' : '#f8f9fb',
                                      transition: 'all 0.3s ease-in-out',
                                    }}
                                  >
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={isGroupSelected}
                                          onChange={() => handleToggleMainGroup(groupName)}
                                          disabled={isFieldReadOnly('subjects')}
                                          sx={{ color: isGroupSelected ? '#4caf50' : undefined }}
                                        />
                                      }
                                      label={
                                        <Box sx={{ flex: 1, ml: 1 }}>
                                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#0168EA', mb: 0.5 }}>
                                            {groupName}
                                          </Typography>
                                          {groupData.groupCode && (
                                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                              {t('group_code')}: {groupData.groupCode}
                                            </Typography>
                                          )}
                                          {isGroupSelected && (() => {
                                            // Get count of subjects from this specific group
                                            const subjectsInThisGroup = formData.subjects.filter(storedValue =>
                                              storedValue.startsWith(`${groupName}|`)
                                            );
                                            return subjectsInThisGroup.length > 0 ? (
                                              <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 500, display: 'block', mt: 0.5 }}>
                                                {t('selected_subjects')}: {subjectsInThisGroup.length}/3
                                              </Typography>
                                            ) : null;
                                          })()}
                                        </Box>
                                      }
                                      sx={{ flex: 1, m: 0 }}
                                    />
                                    {isGroupSelected && (
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setExpandedGroups(prev => {
                                            const newSet = new Set(prev);
                                            if (newSet.has(groupName)) {
                                              newSet.delete(groupName);
                                            } else {
                                              newSet.add(groupName);
                                            }
                                            return newSet;
                                          });
                                        }}
                                      >
                                        {isGroupExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                      </IconButton>
                                    )}
                                  </Box>

                                  {/* Group Content - Only show when selected */}
                                  {isGroupSelected && (
                                    <Collapse in={isGroupExpanded}>
                                      <Box sx={{ p: 2, backgroundColor: '#ffffff' }}>
                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                          {t('select_max_3_subjects_from_group') || 'You can select maximum 3 subjects from this group.'}
                                        </Typography>
                                        {groupData.hasSubgroups && groupData.subgroups ? (
                                          // Show subgroups
                                          groupData.subgroups.map((subgroup: any) => {
                                            const subgroupKey = `${groupName}-${subgroup.subgroupName}`;
                                            const isSubgroupExpanded = expandedSubgroups.has(subgroupKey);

                                            return (
                                              <Box key={subgroup.subgroupName} sx={{ mb: 2 }}>
                                                <Box
                                                  onClick={() => handleToggleSubgroup(groupName, subgroup.subgroupName)}
                                                  sx={{
                                                    p: 1.5,
                                                    cursor: 'pointer',
                                                    backgroundColor: isSubgroupExpanded ? '#f0f7ff' : '#fafafa',
                                                    borderRadius: 1,
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    mb: 1,
                                                    '&:hover': {
                                                      backgroundColor: '#f0f7ff',
                                                    }
                                                  }}
                                                >
                                                  <Box sx={{ flex: 1 }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                                      {t('subgroup')} {subgroup.subgroupName}
                                                    </Typography>
                                                    {subgroup.description && (
                                                      <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, fontSize: '0.875rem' }}>
                                                        {subgroup.description}
                                                      </Typography>
                                                    )}
                                                  </Box>
                                                  <IconButton size="small">
                                                    {isSubgroupExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                  </IconButton>
                                                </Box>

                                                <Collapse in={isSubgroupExpanded}>
                                                  <Box sx={{ pl: 2, pr: 2, pb: 1 }}>
                                                    {(() => {
                                                      // Get count of subjects from this specific group
                                                      const subjectsInThisGroup = formData.subjects.filter(storedValue =>
                                                        storedValue.startsWith(`${groupName}|`)
                                                      );

                                                      // For HUMANITIES GROUP, calculate Subgroup A and B counts
                                                      let subgroupACount = 0;
                                                      let subgroupBCount = 0;
                                                      let isSubgroupBDisabled = false;

                                                      if (groupName === "HUMANITIES GROUP") {
                                                        const subgroupACodes = ["110", "120", "130", "140"];
                                                        const subgroupBCodes = ["150", "151", "162", "163", "164", "165", "166", "167", "168"];

                                                        subjectsInThisGroup.forEach(storedValue => {
                                                          const code = extractSubjectCode(storedValue);
                                                          if (subgroupACodes.includes(code)) {
                                                            subgroupACount++;
                                                          } else if (subgroupBCodes.includes(code)) {
                                                            subgroupBCount++;
                                                          }
                                                        });

                                                        // Disable Subgroup B if 3 subjects are selected from Subgroup A
                                                        if (subgroup.subgroupName === "B") {
                                                          isSubgroupBDisabled = subgroupACount >= 3;
                                                        }
                                                      }

                                                      // For COMMERCE GROUP, calculate Subgroup A and B counts
                                                      if (groupName === "COMMERCE GROUP") {
                                                        const subgroupACodes = ["310", "320"]; // Business Studies, Book Keeping & Accountancy
                                                        const subgroupBCodes = ["150", "151", "140"]; // Mathematics, Informatic Practices, Economics

                                                        subjectsInThisGroup.forEach(storedValue => {
                                                          const code = extractSubjectCode(storedValue);
                                                          if (subgroupACodes.includes(code)) {
                                                            subgroupACount++;
                                                          } else if (subgroupBCodes.includes(code)) {
                                                            subgroupBCount++;
                                                          }
                                                        });

                                                        // Disable Subgroup B if not all 2 subjects from Subgroup A are selected
                                                        if (subgroup.subgroupName === "B") {
                                                          isSubgroupBDisabled = subgroupACount < 2;
                                                        }
                                                      }

                                                      // For LANGUAGE GROUP, calculate Subgroup A and B counts
                                                      if (groupName === "LANGUAGE GROUP") {
                                                        const subgroupACodes = ["051", "052", "053", "054", "055"]; // Hindi, English, Sanskrit, Marathi, Urdu
                                                        const subgroupBCodes = ["110", "120", "130", "140", "166"]; // History, Geography, Political Science, Economics, Sociology

                                                        subjectsInThisGroup.forEach(storedValue => {
                                                          const code = extractSubjectCode(storedValue);
                                                          if (subgroupACodes.includes(code)) {
                                                            subgroupACount++;
                                                          } else if (subgroupBCodes.includes(code)) {
                                                            subgroupBCount++;
                                                          }
                                                        });

                                                        // Disable Subgroup B if not exactly 1 subject from Subgroup A is selected
                                                        if (subgroup.subgroupName === "B") {
                                                          isSubgroupBDisabled = subgroupACount !== 1;
                                                        }
                                                      }

                                                      // For LANGUAGE GROUP Subgroup A, filter out subjects already selected in LANGUAGE (first step)
                                                      // But always show subjects that are already selected in this subgroup
                                                      let filteredSubgroupSubjects = subgroup.subjects || [];
                                                      if (groupName === "LANGUAGE GROUP" && subgroup.subgroupName === "A") {
                                                        const selectedLanguageCodes = getSelectedLanguageSubjects().map(storedValue => {
                                                          const code = extractSubjectCode(storedValue);
                                                          return String(code).trim(); // Ensure string and trim
                                                        });

                                                        // Get subject codes that are already selected in this subgroup
                                                        const selectedInThisSubgroup = subjectsInThisGroup.map(storedValue => {
                                                          const code = extractSubjectCode(storedValue);
                                                          return String(code).trim(); // Ensure string and trim
                                                        });

                                                        // Filter: exclude languages selected in first step, but always include subjects already selected in this subgroup
                                                        filteredSubgroupSubjects = (subgroup.subjects || []).filter((subject: any) => {
                                                          // Normalize subject code for comparison
                                                          const subjectCode = String(subject.subjectCode || subject.originalCode || '').trim();
                                                          const isSelectedInThisSubgroup = selectedInThisSubgroup.includes(subjectCode);
                                                          const isSelectedInLanguageStep = selectedLanguageCodes.includes(subjectCode);
                                                          // Show if: already selected in this subgroup OR not selected in language step
                                                          return isSelectedInThisSubgroup || !isSelectedInLanguageStep;
                                                        });
                                                      }

                                                      // Determine max selection and current count based on subgroup
                                                      let maxSelection = 3;
                                                      let currentCount = subjectsInThisGroup.length;

                                                      if (groupName === "HUMANITIES GROUP") {
                                                        if (subgroup.subgroupName === "A") {
                                                          maxSelection = 3; // Can select 2 or 3 from Subgroup A
                                                          currentCount = subgroupACount;
                                                        } else if (subgroup.subgroupName === "B") {
                                                          maxSelection = 1; // Can select only 1 from Subgroup B
                                                          currentCount = subgroupBCount;
                                                        }
                                                      }
                                                      
                                                      if (groupName === "COMMERCE GROUP") {
                                                        if (subgroup.subgroupName === "A") {
                                                          maxSelection = 2; // Must select both from Subgroup A
                                                          currentCount = subgroupACount;
                                                        } else if (subgroup.subgroupName === "B") {
                                                          maxSelection = 1; // Can select only 1 from Subgroup B
                                                          currentCount = subgroupBCount;
                                                        }
                                                      }
                                                      
                                                      if (groupName === "LANGUAGE GROUP") {
                                                        if (subgroup.subgroupName === "A") {
                                                          maxSelection = 1; // Must select exactly 1 from Subgroup A
                                                          currentCount = subgroupACount;
                                                        } else if (subgroup.subgroupName === "B") {
                                                          maxSelection = 2; // Must select exactly 2 from Subgroup B
                                                          currentCount = subgroupBCount;
                                                        }
                                                      }

                                                      return (
                                                        <Box>
                                                          {/* Validation messages for HUMANITIES GROUP */}
                                                          {groupName === "HUMANITIES GROUP" && subgroup.subgroupName === "A" && (
                                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                                                              {subgroupACount < 2
                                                                ? `Please select at least 2 subjects (currently: ${subgroupACount}/2-3)`
                                                                : subgroupACount === 2
                                                                  ? `Selected: ${subgroupACount}/3. You can select one more from Subgroup A, or select one from Subgroup B.`
                                                                  : `Selected: ${subgroupACount}/3. Subgroup B is now disabled.`
                                                              }
                                                            </Typography>
                                                          )}
                                                          {groupName === "HUMANITIES GROUP" && subgroup.subgroupName === "B" && (
                                                            <Typography variant="body2" color={isSubgroupBDisabled ? "error" : "textSecondary"} sx={{ mb: 1, fontStyle: 'italic' }}>
                                                              {isSubgroupBDisabled
                                                                ? "Subgroup B is disabled because 3 subjects are selected from Subgroup A."
                                                                : subgroupACount === 2
                                                                  ? `Please select exactly 1 subject from Subgroup B (currently: ${subgroupBCount}/1)`
                                                                  : `Subgroup B is only available when exactly 2 subjects are selected from Subgroup A.`
                                                              }
                                                            </Typography>
                                                          )}

                                                          {/* Validation messages for COMMERCE GROUP */}
                                                          {groupName === "COMMERCE GROUP" && subgroup.subgroupName === "A" && (
                                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                                                              {subgroupACount < 2
                                                                ? `Please select both subjects from Subgroup A (currently: ${subgroupACount}/2)`
                                                                : `Selected: ${subgroupACount}/2. Both subjects from Subgroup A are selected. Now select one from Subgroup B.`
                                                              }
                                                            </Typography>
                                                          )}
                                                          {groupName === "COMMERCE GROUP" && subgroup.subgroupName === "B" && (
                                                            <Typography variant="body2" color={isSubgroupBDisabled ? "error" : "textSecondary"} sx={{ mb: 1, fontStyle: 'italic' }}>
                                                              {isSubgroupBDisabled
                                                                ? "Subgroup B is disabled. Please select both subjects from Subgroup A first."
                                                                : subgroupACount === 2
                                                                  ? `Please select exactly 1 subject from Subgroup B (currently: ${subgroupBCount}/1)`
                                                                  : `Subgroup B is only available when both subjects are selected from Subgroup A.`
                                                              }
                                                            </Typography>
                                                          )}

                                                          {/* Validation messages for LANGUAGE GROUP */}
                                                          {groupName === "LANGUAGE GROUP" && subgroup.subgroupName === "A" && (
                                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                                                              {subgroupACount < 1
                                                                ? `Please select exactly 1 language from Subgroup A (currently: ${subgroupACount}/1)`
                                                                : `Selected: ${subgroupACount}/1. One language from Subgroup A is selected. Now select two from Subgroup B.`
                                                              }
                                                            </Typography>
                                                          )}
                                                          {groupName === "LANGUAGE GROUP" && subgroup.subgroupName === "B" && (
                                                            <Typography variant="body2" color={isSubgroupBDisabled ? "error" : "textSecondary"} sx={{ mb: 1, fontStyle: 'italic' }}>
                                                              {isSubgroupBDisabled
                                                                ? "Subgroup B is disabled. Please select exactly 1 language from Subgroup A first."
                                                                : subgroupACount === 1
                                                                  ? `Please select exactly 2 subjects from Subgroup B (currently: ${subgroupBCount}/2)`
                                                                  : `Subgroup B is only available when exactly 1 language is selected from Subgroup A.`
                                                              }
                                                            </Typography>
                                                          )}
                                                          <Box sx={{ opacity: isSubgroupBDisabled ? 0.5 : 1, pointerEvents: isSubgroupBDisabled ? 'none' : 'auto' }}>
                                                            {renderSubjects(
                                                              filteredSubgroupSubjects,
                                                              maxSelection,
                                                              currentCount,
                                                              handleToggleMainGroupSubject,
                                                              true, // Check total limit
                                                              groupName, // Pass groupName for proper validation
                                                              isSubgroupBDisabled // Pass disabled state
                                                            )}
                                                          </Box>
                                                        </Box>
                                                      );
                                                    })()}
                                                  </Box>
                                                </Collapse>
                                              </Box>
                                            );
                                          })
                                        ) : (
                                          // Show subjects directly
                                          (() => {
                                            // Get count of subjects from this specific group
                                            const subjectsInThisGroup = formData.subjects.filter(storedValue =>
                                              storedValue.startsWith(`${groupName}|`)
                                            );
                                            return renderSubjects(
                                              groupData.subjects || [],
                                              3,
                                              subjectsInThisGroup.length,
                                              handleToggleMainGroupSubject,
                                              true, // Check total limit
                                              groupName // Pass groupName to ensure proper limit enforcement
                                            );
                                          })()
                                        )}
                                      </Box>
                                    </Collapse>
                                  )}
                                </Paper>
                              );
                            })()}
                          </>
                        )}


                        {/* Step 3: Additional and NSQF Groups - Show for all streams */}
                        {(showOptionalGroups || selectedStream) && (
                          <>
                            <Typography variant="subtitle2" sx={{ mb: 2, color: '#0168EA', fontWeight: 600, mt: 3 }}>
                              {t('optional_subjects') || 'Optional Subjects (Select One)'}
                            </Typography>

                            {/* Additional Subject Group */}
                            {grouped.additionalGroup && (() => {
                              const selectedSubjectCodes = getSelectedSubjectCodes();
                              const selectedMainGroupNames = getSelectedMainGroupSubjectNames();
                              const isAdditionalSelected = selectedOptional && selectedOptional.startsWith('ADDITIONAL SUBJECT (OPTIONAL)|');
                              const isNSQFSelected = selectedOptional && selectedOptional.startsWith('NSQF SUBJECT (OPTIONAL)|');

                              // Get the selected additional subject code if any
                              const selectedAdditionalCode = isAdditionalSelected ? extractSubjectCode(selectedOptional) : null;

                              // Include all subjects, but show selected one as selected
                              // Only check by code and ensure we only count subjects selected WITH the "ADDITIONAL SUBJECT (OPTIONAL)|" prefix
                              const allAdditionalSubjects = (grouped.additionalGroup.subjects || []).filter((subject: any) => {
                                // Always show the selected additional subject (by code, with ADDITIONAL prefix)
                                if (selectedAdditionalCode && subject.subjectCode === selectedAdditionalCode) {
                                  return true;
                                }
                                // Check if this subject is selected WITH the "ADDITIONAL SUBJECT (OPTIONAL)|" prefix (by code)
                                const isSelectedInAdditional = formData.subjects.some((storedValue: string) => {
                                  return storedValue.startsWith('ADDITIONAL SUBJECT (OPTIONAL)|') &&
                                    extractSubjectCode(storedValue) === subject.subjectCode;
                                });
                                if (isSelectedInAdditional) {
                                  return true; // Show if selected in Additional
                                }
                                // If a subject with the same name is already selected in a main group,
                                // then HIDE / DISABLE it in Additional Subject (OPTIONAL) so it cannot be chosen again
                                const subjectName = subject.subjectName || subject.name;
                                if (subjectName && selectedMainGroupNames.has(subjectName)) {
                                  return false; // Filter out by name match only
                                }
                                // Only filter out if the EXACT same code is selected in a main group or NSQF
                                // Additional subjects have different codes, so this should rarely match
                                // But we check to prevent duplicate selections of the same code
                                const isCodeSelectedInMainOrNSQF = formData.subjects.some((storedValue: string) => {
                                  if (storedValue.startsWith('ADDITIONAL SUBJECT (OPTIONAL)|')) {
                                    return false; // Don't filter if it's selected in Additional
                                  }
                                  const storedCode = extractSubjectCode(storedValue);
                                  return storedCode === subject.subjectCode;
                                });
                                // Show the subject if its code is not already selected elsewhere (except in Additional)
                                return !isCodeSelectedInMainOrNSQF;
                              });

                              return (
                                <Paper
                                  elevation={1}
                                  sx={{
                                    mb: 2,
                                    border: '1px solid',
                                    borderColor: isAdditionalSelected ? '#4caf50' : '#e0e7ff',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    opacity: isNSQFSelected ? 0.6 : 1
                                  }}
                                >
                                  <Box
                                    onClick={() => {
                                      // Only disable if NSQF is selected, otherwise allow interaction
                                      if (!isNSQFSelected) {
                                        const isExpanded = expandedGroups.has(grouped.additionalGroup.groupName);
                                        setExpandedGroups(prev => {
                                          const newSet = new Set(prev);
                                          if (isExpanded) {
                                            newSet.delete(grouped.additionalGroup.groupName);
                                          } else {
                                            newSet.add(grouped.additionalGroup.groupName);
                                          }
                                          return newSet;
                                        });
                                      }
                                    }}
                                    sx={{
                                      p: 2,
                                      cursor: isNSQFSelected ? 'not-allowed' : 'pointer',
                                      backgroundColor: expandedGroups.has(grouped.additionalGroup.groupName) ? '#e3f2fd' : '#f8f9fb',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      opacity: isNSQFSelected ? 0.6 : 1,
                                      '&:hover': {
                                        backgroundColor: isNSQFSelected ? '#f8f9fb' : '#e3f2fd',
                                      }
                                    }}
                                  >
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#0168EA', mb: 0.5 }}>
                                        {grouped.additionalGroup.groupName}
                                      </Typography>
                                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.875rem' }}>
                                        {t('select_one_from_additional_or_nsqf') || 'You can select only ONE subject from Additional or NSQF.'}
                                      </Typography>
                                      {isAdditionalSelected && (
                                        <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 500, display: 'block', mt: 0.5 }}>
                                          {t('selected')}: 1/1
                                        </Typography>
                                      )}
                                    </Box>
                                    <IconButton size="small" disabled={!!isNSQFSelected}>
                                      {expandedGroups.has(grouped.additionalGroup.groupName) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                  </Box>

                                  <Collapse in={expandedGroups.has(grouped.additionalGroup.groupName)}>
                                    <Box sx={{ p: 2, backgroundColor: '#ffffff' }}>
                                      {allAdditionalSubjects.length > 0 ? (
                                        renderSubjects(
                                          allAdditionalSubjects,
                                          1,
                                          isAdditionalSelected ? 1 : 0,
                                          handleToggleOptionalSubject,
                                          true // Check total limit
                                        )
                                      ) : (
                                        <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
                                          {t("all_subjects_already_selected") || "All available subjects have already been selected."}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Collapse>
                                </Paper>
                              );
                            })()}

                            {/* NSQF Subject Group */}
                            {grouped.nsqfGroup && (() => {
                              const selectedSubjectCodes = getSelectedSubjectCodes();
                              const isAdditionalSelected = selectedOptional && selectedOptional.startsWith('ADDITIONAL SUBJECT (OPTIONAL)|');
                              const isNSQFSelected = selectedOptional && selectedOptional.startsWith('NSQF SUBJECT (OPTIONAL)|');

                              // Get the selected NSQF subject code if any
                              const selectedNSQFCode = isNSQFSelected ? extractSubjectCode(selectedOptional) : null;

                              // Include all subjects, but show selected one as selected
                              // Always show subjects that are selected (regardless of which group they were selected from)
                              // Filter out only subjects that are selected in OTHER groups (not NSQF) AND not selected in NSQF
                              const allNSQFSubjects = (grouped.nsqfGroup.subjects || []).filter((subject: any) => {
                                // Always show the selected NSQF subject
                                if (selectedNSQFCode && subject.subjectCode === selectedNSQFCode) {
                                  return true;
                                }
                                // Always show if this subject is selected (check by code, not by group prefix)
                                const isSubjectSelected = formData.subjects.some((storedValue: string) => {
                                  const storedCode = extractSubjectCode(storedValue);
                                  return storedCode === subject.subjectCode;
                                });
                                if (isSubjectSelected) {
                                  return true;
                                }
                                // Filter out subjects selected in other groups (main groups, Additional, etc.)
                                // But keep subjects that are not selected anywhere
                                return !selectedSubjectCodes.has(subject.subjectCode);
                              });

                              return (
                                <Paper
                                  elevation={1}
                                  sx={{
                                    mb: 2,
                                    border: '1px solid',
                                    borderColor: isNSQFSelected ? '#4caf50' : '#e0e7ff',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    opacity: isAdditionalSelected ? 0.6 : 1
                                  }}
                                >
                                  <Box
                                    onClick={() => {
                                      // Only disable if Additional is selected, otherwise allow interaction
                                      if (!isAdditionalSelected) {
                                        const isExpanded = expandedGroups.has(grouped.nsqfGroup.groupName);
                                        setExpandedGroups(prev => {
                                          const newSet = new Set(prev);
                                          if (isExpanded) {
                                            newSet.delete(grouped.nsqfGroup.groupName);
                                          } else {
                                            newSet.add(grouped.nsqfGroup.groupName);
                                          }
                                          return newSet;
                                        });
                                      }
                                    }}
                                    sx={{
                                      p: 2,
                                      cursor: isAdditionalSelected ? 'not-allowed' : 'pointer',
                                      backgroundColor: expandedGroups.has(grouped.nsqfGroup.groupName) ? '#e3f2fd' : '#f8f9fb',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      opacity: isAdditionalSelected ? 0.6 : 1,
                                      '&:hover': {
                                        backgroundColor: isAdditionalSelected ? '#f8f9fb' : '#e3f2fd',
                                      }
                                    }}
                                  >
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#0168EA', mb: 0.5 }}>
                                        {grouped.nsqfGroup.groupName}
                                      </Typography>
                                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.875rem' }}>
                                        {t('select_one_from_additional_or_nsqf') || 'You can select only ONE subject from Additional or NSQF.'}
                                      </Typography>
                                      {isNSQFSelected && (
                                        <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 500, display: 'block', mt: 0.5 }}>
                                          {t('selected')}: 1/1
                                        </Typography>
                                      )}
                                    </Box>
                                    <IconButton size="small" disabled={!!isAdditionalSelected}>
                                      {expandedGroups.has(grouped.nsqfGroup.groupName) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                  </Box>

                                  <Collapse in={expandedGroups.has(grouped.nsqfGroup.groupName)}>
                                    <Box sx={{ p: 2, backgroundColor: '#ffffff' }}>
                                      {allNSQFSubjects.length > 0 ? (
                                        renderSubjects(
                                          allNSQFSubjects,
                                          1,
                                          isNSQFSelected ? 1 : 0,
                                          handleToggleOptionalSubject,
                                          true // Check total limit
                                        )
                                      ) : (
                                        <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
                                          {t("all_subjects_already_selected") || "All available subjects have already been selected."}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Collapse>
                                </Paper>
                              );
                            })()}
                          </>
                        )}
                      </Box>
                    );
                  })()}
                </Grid>
              ) : (
                /* For Other Classes: Show Dropdown */
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label={t('add_subject')}
                    name="addSubject"
                    value={subjectSelectValue}
                    onChange={handleChange}
                    size="small"
                    margin="dense"
                    sx={{ backgroundColor: '#f8f9fb' }}
                    disabled={isFieldReadOnly('subjects') || subjectsLoading || !selectedClass || !classSubjects || classSubjects.length === 0 || formData.subjects.length >= maxSubjectsAllowed}
                    helperText={
                      subjectsLoading
                        ? t("loading_subjects")
                        : !selectedClass
                          ? t("please_select_class_first")
                          : !classSubjects || classSubjects.length === 0
                            ? t("no_subjects_available_for_class")
                            : (formData.subjects.length >= maxSubjectsAllowed)
                              ? t("max_subjects_reached",{count:maxSubjectsAllowed})
                              : t('select_subject_to_add')
                    }
                    SelectProps={{
                      open: subjectDropdownOpen,
                      onOpen: () => setSubjectDropdownOpen(true),
                      onClose: (event: any) => {
                        // Check if the close was triggered by clicking outside (backdrop)
                        // If it was a menu item click, the onClick handler will prevent this
                        const target = event?.target as HTMLElement;
                        if (!target || !target.closest('.MuiMenuItem-root')) {
                          setSubjectDropdownOpen(false);
                        }
                      },
                      MenuProps: {
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            zIndex: 9999,
                          },
                        },
                      },
                    }}
                  >
                    {subjectsLoading ? (
                      <MenuItem disabled>{t("loading_subjects")}</MenuItem>
                    ) : !classSubjects || classSubjects.length === 0 ? (
                      <MenuItem disabled>{t("no_subjects_available")}</MenuItem>
                    ) : (() => {
                      const grouped = getGroupedSubjectOptions();
                      const hasFirstLanguages = grouped.firstLanguages.length > 0;
                      const hasVocational = grouped.vocational.length > 0;
                      const hasOther = grouped.other.length > 0;
                      const hasGroups = isEleventhTwelfthClass && Object.keys(grouped.groups || {}).length > 0;

                      const menuItems: React.ReactNode[] = [];

                      // For Class 11th/12th, show groups by their actual names
                      if (hasGroups) {
                        Object.entries(grouped.groups).forEach(([groupName, subjects]) => {
                          if (subjects.length > 0) {
                            menuItems.push(
                              <ListSubheader
                                key={`${groupName}-header`}
                                disableSticky
                                sx={{
                                  backgroundColor: '#f5f5f5',
                                  fontWeight: 'bold',
                                  pointerEvents: 'none',
                                  userSelect: 'none'
                                }}
                              >
                                {groupName}
                              </ListSubheader>
                            );
                            subjects.forEach((option: any) => {
                              const isSelected = formData.subjects.includes(option.value);
                              menuItems.push(
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                  onClick={(e) => {
                                    // Prevent default close behavior and event bubbling
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!isSelected) {
                                      // Set flag to prevent Select's onChange from firing
                                      isManualSelectionRef.current = true;
                                      handleAddSubject(option.value);
                                      setSubjectSelectValue('');
                                      // Keep dropdown open
                                      setTimeout(() => setSubjectDropdownOpen(true), 10);
                                    }
                                  }}
                                  sx={{
                                    backgroundColor: isSelected ? '#87CEEB' : 'transparent',
                                    '&:hover': {
                                      backgroundColor: isSelected ? '#87CEEB' : 'rgba(0, 0, 0, 0.04)',
                                    },
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                  }}
                                >
                                  <span>{option.label}</span>
                                  {isSelected && (
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const index = formData.subjects.indexOf(option.value);
                                        if (index !== -1) {
                                          handleRemoveSubject(index);
                                        }
                                        // Keep dropdown open after removal
                                        setTimeout(() => setSubjectDropdownOpen(true), 10);
                                      }}
                                      onMouseDown={(e) => {
                                        // Prevent the MenuItem's onClick from firing
                                        e.preventDefault();
                                        e.stopPropagation();
                                      }}
                                      sx={{
                                        ml: 1,
                                        color: '#d32f2f',
                                        '&:hover': {
                                          backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                        },
                                      }}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </MenuItem>
                              );
                            });
                          }
                        });
                      }

                      // For other classes, use the original categorization
                      if (hasFirstLanguages) {
                        menuItems.push(
                          <ListSubheader
                            key="languages-header"
                            disableSticky
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                              pointerEvents: 'none',
                              userSelect: 'none'
                            }}
                          >
                            {t('languages')}
                          </ListSubheader>
                        );
                        grouped.firstLanguages.forEach((option: any) => {
                          const isSelected = formData.subjects.includes(option.value);
                          menuItems.push(
                            <MenuItem
                              key={option.value}
                              value={option.value}
                              onClick={(e) => {
                                // Prevent default close behavior and event bubbling
                                e.preventDefault();
                                e.stopPropagation();
                                if (!isSelected) {
                                  // Set flag to prevent Select's onChange from firing
                                  isManualSelectionRef.current = true;
                                  handleAddSubject(option.value);
                                  setSubjectSelectValue('');
                                  // Keep dropdown open
                                  setTimeout(() => setSubjectDropdownOpen(true), 10);
                                }
                              }}
                              sx={{
                                backgroundColor: isSelected ? '#87CEEB' : 'transparent',
                                '&:hover': {
                                  backgroundColor: isSelected ? '#87CEEB' : 'rgba(0, 0, 0, 0.04)',
                                },
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <span>{option.label}</span>
                              {isSelected && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const index = formData.subjects.indexOf(option.value);
                                    if (index !== -1) {
                                      handleRemoveSubject(index);
                                    }
                                    // Keep dropdown open after removal
                                    setTimeout(() => setSubjectDropdownOpen(true), 10);
                                  }}
                                  onMouseDown={(e) => {
                                    // Prevent the MenuItem's onClick from firing
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  sx={{
                                    ml: 1,
                                    color: '#d32f2f',
                                    '&:hover': {
                                      backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                    },
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              )}
                            </MenuItem>
                          );
                        });
                      }

                      if (hasVocational) {
                        menuItems.push(
                          <ListSubheader
                            key="vocational-header"
                            disableSticky
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                              pointerEvents: 'none',
                              userSelect: 'none'
                            }}
                          >
                            {t('vocational_subjects')}
                          </ListSubheader>
                        );
                        grouped.vocational.forEach((option: any) => {
                          const isSelected = formData.subjects.includes(option.value);
                          menuItems.push(
                            <MenuItem
                              key={option.value}
                              value={option.value}
                              onClick={(e) => {
                                // Prevent default close behavior and event bubbling
                                e.preventDefault();
                                e.stopPropagation();
                                if (!isSelected) {
                                  // Set flag to prevent Select's onChange from firing
                                  isManualSelectionRef.current = true;
                                  handleAddSubject(option.value);
                                  setSubjectSelectValue('');
                                  // Keep dropdown open
                                  setTimeout(() => setSubjectDropdownOpen(true), 10);
                                }
                              }}
                              sx={{
                                backgroundColor: isSelected ? '#87CEEB' : 'transparent',
                                '&:hover': {
                                  backgroundColor: isSelected ? '#87CEEB' : 'rgba(0, 0, 0, 0.04)',
                                },
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <span>{option.label}</span>
                              {isSelected && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const index = formData.subjects.indexOf(option.value);
                                    if (index !== -1) {
                                      handleRemoveSubject(index);
                                    }
                                    // Keep dropdown open after removal
                                    setTimeout(() => setSubjectDropdownOpen(true), 10);
                                  }}
                                  onMouseDown={(e) => {
                                    // Prevent the MenuItem's onClick from firing
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  sx={{
                                    ml: 1,
                                    color: '#d32f2f',
                                    '&:hover': {
                                      backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                    },
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              )}
                            </MenuItem>
                          );
                        });
                      }

                      if (hasOther) {
                        menuItems.push(
                          <ListSubheader
                            key="other-header"
                            disableSticky
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                              pointerEvents: 'none',
                              userSelect: 'none'
                            }}
                          >
                            {t('other_subjects')}
                          </ListSubheader>
                        );
                        grouped.other.forEach((option: any) => {
                          const isSelected = formData.subjects.includes(option.value);
                          menuItems.push(
                            <MenuItem
                              key={option.value}
                              value={option.value}
                              onClick={(e) => {
                                // Prevent default close behavior and event bubbling
                                e.preventDefault();
                                e.stopPropagation();
                                if (!isSelected) {
                                  // Set flag to prevent Select's onChange from firing
                                  isManualSelectionRef.current = true;
                                  handleAddSubject(option.value);
                                  setSubjectSelectValue('');
                                  // Keep dropdown open
                                  setTimeout(() => setSubjectDropdownOpen(true), 10);
                                }
                              }}
                              sx={{
                                backgroundColor: isSelected ? '#87CEEB' : 'transparent',
                                '&:hover': {
                                  backgroundColor: isSelected ? '#87CEEB' : 'rgba(0, 0, 0, 0.04)',
                                },
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <span>{option.label}</span>
                              {isSelected && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const index = formData.subjects.indexOf(option.value);
                                    if (index !== -1) {
                                      handleRemoveSubject(index);
                                    }
                                    // Keep dropdown open after removal
                                    setTimeout(() => setSubjectDropdownOpen(true), 10);
                                  }}
                                  onMouseDown={(e) => {
                                    // Prevent the MenuItem's onClick from firing
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  sx={{
                                    ml: 1,
                                    color: '#d32f2f',
                                    '&:hover': {
                                      backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                    },
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              )}
                            </MenuItem>
                          );
                        });
                      }

                      if (!hasGroups && !hasFirstLanguages && !hasVocational && !hasOther) {
                        menuItems.push(
                          <MenuItem key="no-subjects" disabled>{t("no_subjects_available")}</MenuItem>
                        );
                      }

                      return menuItems;
                    })()}
                  </TextField>
                </Grid>
              )}

              {/* Student Photo and Signature Upload Section - Right side of Subject Dropdown */}
              <Grid item xs={12} md={8}>
                <Grid container spacing={1}>
                  {/* Student Photo Upload Section */}
                  <Grid item xs={12} sm={6}>
                    <Paper
                      variant="outlined"
                      sx={{
                        height: '40px', // Match TextField height
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1.5,
                        bgcolor: '#f8f9fb', // Match TextField background
                        mb: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {imagePreview || authenticatedImageUrl ? (
                          <Avatar
                            src={imagePreview || authenticatedImageUrl || undefined}
                            sx={{
                              width: 24,
                              height: 24,
                              border: '1px solid #0168EA',
                              cursor: 'pointer'
                            }}
                            onClick={() => setImagePreviewModal(true)}
                          />
                        ) : (
                          <PhotoCameraIcon fontSize="small" color="primary" />
                        )}
                        <Typography variant="body2">{t('student_photo')}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Delete Button - only show when image exists and not read-only */}
                        {(imagePreview || authenticatedImageUrl) && !isReadOnly && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleRemoveImage}
                            sx={{
                              minWidth: 'auto',
                              p: '4px 8px',
                              borderColor: '#f44336',
                              color: '#f44336',
                              '&:hover': {
                                backgroundColor: '#ffebee',
                                borderColor: '#d32f2f'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </Button>
                        )}
                        {/* Upload Button - disabled when read-only */}
                        {!isReadOnly && (
                          <Button
                            variant="outlined"
                            component="label"
                            size="small"
                            sx={{ minWidth: 'auto', p: '4px 8px' }}
                          >
                            {imagePreview || authenticatedImageUrl ? (
                              <CheckCircleIcon fontSize="small" color="success" />
                            ) : (
                              <CloudUploadIcon fontSize="small" />
                            )}
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={handleImageChange}
                            />
                          </Button>
                        )}
                      </Box>
                    </Paper>
                    {imageError && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {imageError}
                      </Typography>
                    )}
                  </Grid>

                  {/* Student Signature Upload Section */}
                  <Grid item xs={12} sm={6}>
                    <Paper
                      variant="outlined"
                      sx={{
                        height: '40px', // Match TextField height
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1.5,
                        bgcolor: '#f8f9fb' // Match TextField background
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {signaturePreview || authenticatedSignatureUrl ? (
                          <Avatar
                            src={signaturePreview || authenticatedSignatureUrl || undefined}
                            sx={{
                              width: 24,
                              height: 24,
                              border: '1px solid #0168EA',
                              cursor: 'pointer'
                            }}
                            onClick={() => setSignaturePreviewModal(true)}
                          />
                        ) : (
                          <PhotoCameraIcon fontSize="small" color="primary" />
                        )}
                        <Typography variant="body2">{t('student_signature')}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Delete Button - only show when signature exists and not read-only */}
                        {(signaturePreview || authenticatedSignatureUrl) && !isReadOnly && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleRemoveSignature}
                            sx={{
                              minWidth: 'auto',
                              p: '4px 8px',
                              borderColor: '#f44336',
                              color: '#f44336',
                              '&:hover': {
                                backgroundColor: '#ffebee',
                                borderColor: '#d32f2f'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </Button>
                        )}
                        {/* Upload Button - disabled when read-only */}
                        {!isReadOnly && (
                          <Button
                            variant="outlined"
                            component="label"
                            size="small"
                            sx={{ minWidth: 'auto', p: '4px 8px' }}
                          >
                            {signaturePreview || authenticatedSignatureUrl ? (
                              <CheckCircleIcon fontSize="small" color="success" />
                            ) : (
                              <CloudUploadIcon fontSize="small" />
                            )}
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={handleSignatureChange}
                            />
                          </Button>
                        )}
                      </Box>
                    </Paper>
                    {signatureError && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {signatureError}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Grid>

              {/* Selected Subjects Display - Below all three fields */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: '#0168EA', fontWeight: 600 }}>
                    {t('selected_subjects')} ({formData.subjects.length}/6)
                  </Typography>
                  {formData.subjects.length >= maxSubjectsAllowed && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 500 }}>
                      {t('max_subjects_reached',{count:maxSubjectsAllowed})}
                    </Typography>
                  )}
                </Box>

                {formData.subjects.length === 0 ? (
                  <Box sx={{
                    p: 2,
                    textAlign: 'center',
                    backgroundColor: '#f8f9fb',
                    borderRadius: 1,
                    border: '1px dashed #ccc'
                  }}>
                    <Typography variant="body2" color="textSecondary">
                      {t('no_subjects_selected')}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {getSortedSubjects().map((subjectCode, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1.5,
                          backgroundColor: '#e3f2fd',
                          borderRadius: 2,
                          border: '1px solid #90caf9',
                          '&:hover': {
                            backgroundColor: '#bbdefb'
                          }
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getSubjectDisplayName(subjectCode)}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleRemoveSubject(index)}
                          disabled={isStudentProfileUpdate || shouldLockSubjectsForMarksheets}
                          sx={{
                            minWidth: 'auto',
                            p: 1,
                            borderColor: '#f44336',
                            color: '#f44336',
                            '&:hover': {
                              backgroundColor: '#ffebee',
                              borderColor: '#d32f2f'
                            }
                          }}
                        >
                          ×
                        </Button>
                      </Box>
                    ))}
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 1.5, gap: 2, backgroundColor: '#f8f9fb' }}>
          <Button
            type="button"
            onClick={() => {
              // Save draft data when canceling (only for create mode and if form has data)
              if (!isEdit && !isStudentEdit) {
                // Check if form has any meaningful data (not just empty/default values)
                const hasData = formData.studentName ||
                  formData.fatherName ||
                  formData.motherName ||
                  formData.dateOfBirth ||
                  formData.gender ||
                  formData.mobileNo ||
                  formData.medium ||
                  formData.applicationNo ||
                  formData.rollNo ||
                  formData.enrolmentNo ||
                  (formData.subjects && formData.subjects.length > 0);

                if (hasData) {
                  saveApplicationFormDraft(formData);
                }
              }
              onClose();
            }}
            variant="outlined"
            size="large"
            sx={{
              minWidth: 120,
              borderColor: '#3092e3',
              color: '#3092e3',
              '&:hover': {
                borderColor: '#0056d3',
                backgroundColor: 'rgba(1, 104, 234, 0.04)'
              }
            }}
          >
            {t('cancel')}
          </Button>
          {!isReadOnly && (
            <Button
              type="submit"
              variant="contained"
              size="large"
              sx={{
                minWidth: 120,
                backgroundColor: '#3092e3',
                boxShadow: '0 4px 12px rgba(66, 165, 245, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0056d3 0%, #2f7de8 100%)',
                  boxShadow: '0 6px 16px rgba(1, 104, 234, 0.4)'
                }
              }}
            >
              {isEdit ? t('update') : t('submit')}
            </Button>
          )}
        </DialogActions>

        {/* Image Preview Modal */}
        <Dialog
          open={imagePreviewModal}
          onClose={() => setImagePreviewModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6">{t('student_photo_preview')}</Typography>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
            {(imagePreview || authenticatedImageUrl) && (
              <Box
                component="img"
                src={imagePreview || authenticatedImageUrl || undefined}
                alt="Student Photo Preview"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImagePreviewModal(false)} variant="contained">
              {t('close')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Signature Preview Modal */}
        <Dialog
          open={signaturePreviewModal}
          onClose={() => setSignaturePreviewModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6">{t('student_signature')} {t('preview') || 'Preview'}</Typography>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
            {(signaturePreview || authenticatedSignatureUrl) && (
              <Box
                component="img"
                src={signaturePreview || authenticatedSignatureUrl || undefined}
                alt="Student Signature Preview"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSignaturePreviewModal(false)} variant="contained">
              {t('close')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Sibling Confirmation Dialog */}
        <Dialog
          open={siblingDialogOpen}
          onClose={() => setSiblingDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 1,
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>
            Sibling Check
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              This mobile number belongs to <strong>{siblingStudentName}</strong>. Are you his/her sibling?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={handleSiblingNo} 
              variant="outlined" 
              color="error"
              sx={{ borderRadius: 2 }}
            >
              No, enter different number
            </Button>
            <Button 
              onClick={handleSiblingYes} 
              variant="contained" 
              color="primary"
              sx={{ borderRadius: 2 }}
              autoFocus
            >
              Yes, proceed
            </Button>
          </DialogActions>
        </Dialog>
      </Dialog>
    </LocalizationProvider>
  );
};

